// src/pages/AdminPage.tsx
// Internal admin portal. Four sections: Open Orders (authorized-but-uncaptured
// orders in the free-cancellation window, with a free-void action),
// Square↔Printful SKU sync, Printful store-catalog health (good standing vs.
// discontinuation), and the Netlify tax blobs (economic-nexus tracking).
//
// SECURITY: this page is only reachable / rendered when the logged-in account is
// on the admin allowlist (useCustomerAuth().isAdmin). That gate is UX only — the
// actual protection is that every admin endpoint re-verifies the signed JWT
// server-side (netlify/functions/utils/admin-auth.js). A non-admin who forces
// their way to /admin sees the "not authorized" screen and, even if they bypass
// that, gets 401s from every data call.

import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCustomerAuth } from "../contexts/CustomerAuthContext";
import { useCatalog } from "../contexts/CatalogContext";
import PageContainer, { GapSize } from "../components/PageContainer";
import OrderWrapper from "../components/OrderWrapper";
import ConfirmationModal from "../components/ConfirmationModal";
import AdminNavMenu, {
  AdminTab,
  ADMIN_TAB_IDS,
} from "../components/AdminNavMenu";
import { useTabParam } from "../hooks/useTabParam";
import { downloadReportHtml, downloadReportCsv } from "../utils/economicReport";
import { downloadPricingCsv } from "../utils/pricingReport";
import {
  getAdminTaxBlobs,
  getAdminFeedHealth,
  getAdminPrintfulCatalog,
  getAdminSkuSync,
  applyAdminSkuSync,
  getAdminSwatchSync,
  applyAdminSwatchSync,
  AdminSwatchSyncResult,
  getAdminSiteConfig,
  setAdminCheckoutEnabled,
  bumpAdminCatalogEpoch,
  setAdminHomepageGroups,
  AdminSiteConfig,
  getAdminOpenOrders,
  getAdminInFlightOrders,
  getAdminShippedOrders,
  getAdminCancelledOrders,
  getAdminOrderDetail,
  searchAdminOrders,
  cancelAdminOpenOrder,
  refundAdminOrder,
  resendAdminEmail,
  getAdminAlerts,
  dismissAdminAlert,
  clearAdminAlerts,
  getAdminReviews,
  moderateAdminReview,
  getAdminReturnRequests,
  updateAdminReturnStatus,
  getCatalogVisibility,
  setItemHidden,
  getAdminPromos,
  savePromoCode,
  deletePromoCode,
  saveSalesperson,
  deleteSalesperson,
  getAdminPromoReport,
  getPromoArchiveIndex,
  getPromoArchive,
  AdminPromoCode,
  AdminSalesperson,
  AdminPromoReport,
  AdminPromoArchiveEntry,
  getAdminTrafficReport,
  getTrafficArchiveIndex,
  getTrafficArchive,
  AdminTrafficReport,
  AdminTrafficArchiveEntry,
  PromoCodeInput,
  getAdminSalesSummary,
  refreshAdminCosts,
  getAdminCostSnapshot,
  getAdminPricingReport,
  AdminPricingReport,
  AdminSalesSummary,
  AdminCostSnapshotMeta,
  AdminCatalogItem,
  AdminReview,
  AdminReviewsFeed,
  AdminTaxBlobs,
  AdminFeedHealth,
  AdminFeedTarget,
  AdminPrintfulCatalog,
  AdminSkuSyncResult,
  AdminOpenOrders,
  AdminOpenOrder,
  AdminOrderDetail,
  AdminAlertsFeed,
  PrintfulHealthStatus,
  AdminReturnRequest,
  AdminReturnRequestsFeed,
} from "../api/admin";

// ---------------------------------------------------------------------------
// Deep linking
//
// Every tab in the portal is addressable: the hamburger tab is `?tab=`, and the
// inner tab row a section may have is `?sub=`. One shared `sub` key (rather than
// one per section) keeps URLs short and means only a single param has to be
// cleared when the outer tab changes; each section validates `sub` against its
// own ids and falls back to its default, so a leftover value from another
// section is harmless.
// ---------------------------------------------------------------------------

const ADMIN_TAB_PARAM = "tab";
const ADMIN_SUBTAB_PARAM = "sub";
// Module-level so the setter identity stays stable across renders.
const CLEAR_ON_TAB_CHANGE = [ADMIN_SUBTAB_PARAM];

// ---------------------------------------------------------------------------
// Small presentational helpers
// ---------------------------------------------------------------------------

const money = (cents: number): string =>
  `$${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STATUS_META: Record<
  PrintfulHealthStatus,
  { label: string; dot: string; chip: string }
> = {
  in_good_standing: {
    label: "Good standing",
    dot: "bg-green-500",
    chip: "bg-green-100 text-green-800",
  },
  discontinuing: {
    label: "Discontinuing",
    dot: "bg-amber-500",
    chip: "bg-amber-100 text-amber-800",
  },
  out_of_stock: {
    label: "Out of stock",
    dot: "bg-orange-500",
    chip: "bg-orange-100 text-orange-800",
  },
  discontinued: {
    label: "Discontinued",
    dot: "bg-red-500",
    chip: "bg-red-100 text-red-800",
  },
  unknown: {
    label: "Unknown",
    dot: "bg-gray-400",
    chip: "bg-gray-100 text-gray-600",
  },
};

const StatusChip: React.FC<{ status: PrintfulHealthStatus }> = ({ status }) => {
  const m = STATUS_META[status] ?? STATUS_META.unknown;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ${m.chip}`}
    >
      <span className={`h-2 w-2 rounded-full ${m.dot}`} />
      {m.label}
    </span>
  );
};

const ProgressBar: React.FC<{ value: number; color: string }> = ({
  value,
  color,
}) => (
  <div className="h-2 w-full rounded-full bg-gray-200 overflow-hidden">
    <div
      className={`h-full ${color} transition-all`}
      style={{ width: `${Math.min(Math.max(value, 0), 1) * 100}%` }}
    />
  </div>
);

const SummaryStat: React.FC<{
  label: string;
  value: React.ReactNode;
  accent?: string;
}> = ({ label, value, accent }) => (
  <div className="rounded-lg border border-accent-light bg-white px-4 py-3 text-center">
    <div className={`text-2xl font-bold ${accent ?? "text-brown-dark"}`}>
      {value}
    </div>
    <div className="text-xs text-gray-500 mt-0.5">{label}</div>
  </div>
);

const ErrorNote: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-rust text-sm">{children}</p>
);

// ---------------------------------------------------------------------------
// Section: Sales & Margin Dashboard
// ---------------------------------------------------------------------------

const RANGE_OPTIONS: { value: number; label: string }[] = [
  { value: 7, label: "Last 7 days" },
  { value: 30, label: "Last 30 days" },
  { value: 90, label: "Last 90 days" },
  { value: 365, label: "Last year" },
  { value: 0, label: "All time" },
];

/** Compact relative age like "3h ago" / "2d ago" from an ISO timestamp. */
const relativeAge = (iso?: string | null): string => {
  if (!iso) return "never";
  const mins = Math.max(
    0,
    Math.floor((Date.now() - new Date(iso).getTime()) / 60000),
  );
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

// Fixed, theme-independent segment colors (validated colorblind-safe as a set —
// see the dataviz palette). NOT theme CSS-vars: those swap per season and can't
// take a Tailwind opacity modifier, which is what made the old bars invisible
// until hover. Order of the stack, bottom → top: cost, margin, shipping, tax.
const SEG_COLORS = {
  cogs: "#2a78d6", // blue   — product cost (COGS)
  margin: "#008300", // green  — profit
  revenue: "#2a78d6", // blue   — product revenue when cost is unknown
  shipping: "#eb6834", // orange — shipping collected
  tax: "#4a3aa7", // violet — sales tax collected
} as const;

type Seg = { key: string; label: string; cents: number; color: string };

/** YYYY-MM-DD → "7/1" (short) or "Jul 1, 2026" (long), parsed as calendar parts
 *  so the UTC day never shifts under the local timezone. */
const fmtDay = (iso: string, style: "short" | "long"): string => {
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  if (style === "short") return `${m}/${d}`;
  const month = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ][m - 1];
  return `${month} ${d}, ${y}`;
};

/** Break a day's collected total into its stacked components (bottom → top). */
const segmentsFor = (p: AdminSalesSummary["timeseries"][number]): Seg[] => {
  const segs: Seg[] = [];
  // Product revenue splits into cost + margin only when the day is fully costed
  // AND actually profitable; a break-even/loss day stays one "revenue" block
  // (you can't draw a negative-height slice) with the margin shown in the tooltip.
  if (p.marginCents != null && p.marginCents > 0) {
    const cogs = p.revenueCents - p.marginCents;
    if (cogs > 0)
      segs.push({
        key: "cogs",
        label: "Product cost",
        cents: cogs,
        color: SEG_COLORS.cogs,
      });
    segs.push({
      key: "margin",
      label: "Margin",
      cents: p.marginCents,
      color: SEG_COLORS.margin,
    });
  } else if (p.revenueCents > 0) {
    segs.push({
      key: "revenue",
      label: "Product revenue",
      cents: p.revenueCents,
      color: SEG_COLORS.revenue,
    });
  }
  if (p.shippingCents > 0)
    segs.push({
      key: "shipping",
      label: "Shipping",
      cents: p.shippingCents,
      color: SEG_COLORS.shipping,
    });
  if (p.taxCents > 0)
    segs.push({
      key: "tax",
      label: "Tax",
      cents: p.taxCents,
      color: SEG_COLORS.tax,
    });
  return segs;
};

/** One row in the hover tooltip: color key · label · value. */
const TipRow: React.FC<{
  color?: string;
  label: string;
  value: string;
  strong?: boolean;
}> = ({ color, label, value, strong }) => (
  <div className="flex items-center gap-2">
    <span
      className="inline-block h-0.5 w-3 shrink-0 rounded-full"
      style={{ background: color ?? "transparent" }}
    />
    <span className={`flex-1 ${strong ? "text-white" : "text-gray-300"}`}>
      {label}
    </span>
    <span
      className={`tabular-nums ${strong ? "font-semibold text-white" : "text-gray-100"}`}
      translate="no"
    >
      {value}
    </span>
  </div>
);

/**
 * Revenue by day — a stacked bar per day whose slices compose the total the
 * customer paid (product cost + margin + shipping + tax). Dependency-free:
 * flex columns sized by percentage height, a styled hover tooltip that itemizes
 * the stack, and a thinned-out date axis.
 */
const RevenueBars: React.FC<{
  points: AdminSalesSummary["timeseries"];
}> = ({ points }) => {
  if (points.length === 0) return null;
  // Cap the number of bars so a year view stays readable.
  const shown = points.slice(-60);
  // Total collected sets the shared vertical scale (it's the full bar height).
  const max = Math.max(...shown.map((p) => p.totalCollectedCents), 1);
  // Only label ~12 days so the axis never crowds; always keep the last day.
  const step = Math.max(1, Math.ceil(shown.length / 12));

  // Legend lists only the components that actually appear in this window.
  const present = new Set<string>();
  shown.forEach((p) => segmentsFor(p).forEach((s) => present.add(s.key)));
  const legend: { key: string; label: string; color: string }[] = [
    { key: "cogs", label: "Product cost", color: SEG_COLORS.cogs },
    { key: "revenue", label: "Product revenue", color: SEG_COLORS.revenue },
    { key: "margin", label: "Margin", color: SEG_COLORS.margin },
    { key: "shipping", label: "Shipping", color: SEG_COLORS.shipping },
    { key: "tax", label: "Tax", color: SEG_COLORS.tax },
    // "Product revenue" and "Product cost" share the blue key; show only one.
  ].filter(
    (l) => present.has(l.key) && !(l.key === "revenue" && present.has("cogs")),
  );

  return (
    <div className="rounded-lg border border-accent-light bg-white p-4">
      {/* items-stretch is load-bearing: the slices are sized with a percentage
          height, which only resolves if the column stretches to the fixed h-40.
          Each column bottom-aligns its stack via justify-end. overflow-visible
          lets a column's hover tooltip escape the plot. */}
      <div className="flex items-stretch gap-[3px] h-40">
        {shown.map((p) => {
          const segs = segmentsFor(p);
          return (
            <div
              key={p.date}
              className="group relative flex flex-1 min-w-0 flex-col justify-end"
            >
              {/* Stack, top → bottom in the DOM (reverse of bottom → top order). */}
              {segs
                .slice()
                .reverse()
                .map((s, i) => (
                  <div
                    key={s.key}
                    className={`w-full transition-[filter] group-hover:brightness-110 ${
                      i === 0 ? "rounded-t-[3px]" : ""
                    } ${i < segs.length - 1 ? "border-b-2 border-white" : ""}`}
                    style={{
                      height: `${(s.cents / max) * 100}%`,
                      minHeight: "3px",
                      background: s.color,
                    }}
                  />
                ))}

              {/* Styled hover/focus tooltip — the overflow home for every value. */}
              <div
                className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-2 hidden w-max -translate-x-1/2 rounded-lg bg-gray-900/95 px-3 py-2 text-[11px] leading-relaxed shadow-lg group-hover:block"
                role="tooltip"
              >
                <div className="mb-1 font-semibold text-white" translate="no">
                  {fmtDay(p.date, "long")}
                </div>
                {p.marginCents != null && p.marginCents <= 0 ? (
                  <>
                    <TipRow
                      color={SEG_COLORS.revenue}
                      label="Product revenue"
                      value={money(p.revenueCents)}
                    />
                    <TipRow
                      label="Margin"
                      value={money(p.marginCents)}
                      color="#e34948"
                    />
                  </>
                ) : p.marginCents != null ? (
                  <>
                    <TipRow
                      color={SEG_COLORS.cogs}
                      label="Product cost"
                      value={money(p.revenueCents - p.marginCents)}
                    />
                    <TipRow
                      color={SEG_COLORS.margin}
                      label="Margin"
                      value={money(p.marginCents)}
                    />
                  </>
                ) : (
                  <TipRow
                    color={SEG_COLORS.revenue}
                    label="Product revenue"
                    value={money(p.revenueCents)}
                  />
                )}
                {p.shippingCents > 0 && (
                  <TipRow
                    color={SEG_COLORS.shipping}
                    label="Shipping"
                    value={money(p.shippingCents)}
                  />
                )}
                {p.taxCents > 0 && (
                  <TipRow
                    color={SEG_COLORS.tax}
                    label="Tax"
                    value={money(p.taxCents)}
                  />
                )}
                <div className="my-1 border-t border-white/15" />
                <TipRow
                  label="Total collected"
                  value={money(p.totalCollectedCents)}
                  strong
                />
                <div className="mt-0.5 text-gray-400">
                  {p.orders} order{p.orders === 1 ? "" : "s"}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Date axis — one thinned-out tick per column slot so labels line up. */}
      <div className="mt-1.5 flex gap-[3px]">
        {shown.map((p, i) => (
          <div
            key={p.date}
            className="min-w-0 flex-1 text-center text-[10px] text-gray-400"
            translate="no"
          >
            {i % step === 0 || i === shown.length - 1
              ? fmtDay(p.date, "short")
              : ""}
          </div>
        ))}
      </div>

      {/* Legend — only the components present in this window. */}
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-gray-500">
        {legend.map((l) => (
          <span key={l.key} className="flex items-center gap-1.5">
            <span
              className="inline-block h-2 w-2 rounded-sm"
              style={{ background: l.color }}
            />
            {l.label}
          </span>
        ))}
      </div>
    </div>
  );
};

/**
 * Catalog-wide pricing & margin report — current price vs Printful blank cost
 * vs a recommended price at the target margin (~2× cost on clean price points,
 * +$3/+$5/+$7 big-size surcharges). Reads the same cost snapshot as the margin
 * tiles, and is deliberately INDEPENDENT of the sales date range below.
 */
const PRICING_TARGETS = [45, 48, 50, 55];

const PricingReportPanel: React.FC<{
  authToken: string;
  snapshotAvailable: boolean;
}> = ({ authToken, snapshotAvailable }) => {
  const [report, setReport] = useState<AdminPricingReport | null>(null);
  const [target, setTarget] = useState(48);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState<"view" | "csv" | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Fetch (or reuse) the report for the given target margin.
  const fetchReport = async (
    kind: "view" | "csv",
    t = target,
  ): Promise<AdminPricingReport | null> => {
    if (report && report.targetPct === t) return report;
    setLoading(kind);
    setError(null);
    const res = await getAdminPricingReport(authToken, t);
    setLoading(null);
    if (!res || res.error || !res.data) {
      setError(res?.error ?? "Failed to load pricing report.");
      return null;
    }
    setReport(res.data);
    return res.data;
  };

  // Changing the target while the table is open refetches in place so the
  // table doesn't blank out.
  const changeTarget = async (t: number) => {
    setTarget(t);
    if (open) {
      const r = await fetchReport("view", t);
      if (!r) setOpen(false);
    } else {
      setReport(null); // stale for the new target — refetch on demand
    }
  };

  const view = async () => {
    if (open) {
      setOpen(false);
      return;
    }
    const r = await fetchReport("view");
    if (r) setOpen(true);
  };

  const downloadCsv = async () => {
    const r = await fetchReport("csv");
    if (r) downloadPricingCsv(r);
  };

  /** "$29.99–33.99" across variations, or a single value when they all match.
   *  The hi end is only dropped from the second number's dollar sign, keeping
   *  the column narrow (mirrors the offline cost report's lo–hi columns). */
  const moneyRange = (lo: number | null, hi: number | null) => {
    if (lo == null) return <span className="text-gray-400">—</span>;
    if (hi == null || hi === lo) return <>{money(lo)}</>;
    return (
      <>
        {money(lo)}
        <span className="text-gray-400">–</span>
        {money(hi).replace("$", "")}
      </>
    );
  };

  const pctRange = (lo: number | null, hi: number | null) => {
    if (lo == null) return "—";
    return hi == null || hi === lo ? `${lo}%` : `${lo}–${hi}%`;
  };

  /** "embroidery_chest_left" → "Left chest" — strip the technique prefix Printful
   *  bakes into some ids, then title-case the rest. */
  const placementLabel = (id: string) =>
    id
      .replace(/^(embroidery|dtg|dtfilm)_/, "")
      .replace(/_dtf$/, "")
      .replace(/_/g, " ")
      .replace(/^\w/, (c) => c.toUpperCase());

  /** Decoration count + a tooltip listing them and the suggested price bump.
   *  Advisory: the blank cost only covers the first decoration, so >1 means the
   *  Cost and Margin columns on this row read optimistic. */
  const placementsCell = (
    p: AdminPricingReport["products"][number],
    targetPct: number,
  ) => {
    const pl = p.placements;
    if (!pl || pl.count === 0) return <span className="text-gray-400">—</span>;

    const lines = pl.list.map((d, i) => {
      const label = placementLabel(d.placement);
      const tech = d.technique ? ` (${d.technique})` : "";
      const price = d.included
        ? "included in blank cost"
        : d.priceCents != null
          ? money(d.priceCents)
          : "no price on file";
      const opts = d.options.length
        ? ` + ${d.options.join(", ")} ${money(d.optionCents)}`
        : "";
      return `  ${i + 1}. ${label}${tech} — ${price}${opts}`;
    });

    // What the blank cost misses: extra decorations, a paid upgrade on the
    // included one, or both. A lone placement with no upgrade misses nothing.
    const missed: string[] = [];
    if (pl.extraCount > 0)
      missed.push(
        `${pl.extraCount} extra decoration${pl.extraCount === 1 ? "" : "s"}`,
      );
    const upgrades = pl.list.flatMap((d) => d.options);
    if (upgrades.length > 0) missed.push(upgrades.join(", "));

    const summary =
      pl.addOnCents === 0
        ? "The blank cost covers this product's only decoration, so Cost and Margin are complete."
        : [
            `The blank cost covers one decoration, so ${missed.join(" and ")}`,
            `add ~${money(pl.addOnCents)}/unit that Cost and Margin DON'T include.`,
            `To hold ${targetPct}% margin, add ~${money(pl.priceImpactCents)} to the recommended price.`,
            pl.priced
              ? ""
              : "At least one placement has no price on file — treat this as a floor.",
            "Estimate — not validated against a real order for this product.",
          ]
            .filter(Boolean)
            .join(" ");

    const title = [
      `${pl.count} decoration${pl.count === 1 ? "" : "s"}:`,
      ...lines,
      "",
      summary,
    ].join("\n");

    return (
      <span
        className={
          pl.addOnCents > 0
            ? "text-amber-600 font-semibold cursor-help underline decoration-dotted"
            : "text-gray-500 cursor-help"
        }
        title={title}
      >
        {pl.count}
        {pl.addOnCents > 0 && ` (+${money(pl.priceImpactCents)})`}
      </span>
    );
  };

  const priceCell = (p: AdminPricingReport["products"][number]) => {
    if (p.recommendedCents == null)
      return <span className="text-gray-400">—</span>;
    const title =
      p.recommendedMarginPct != null
        ? `${p.recommendedMarginPct}% margin at this price`
        : undefined;
    if (p.action === "keep")
      return (
        <span className="text-green-700" title={title}>
          keep {money(p.currentCents)}
        </span>
      );
    return (
      <span className="font-semibold text-brown-dark" title={title}>
        {money(p.recommendedCents)}
      </span>
    );
  };

  /** Non-base sizes as "2XL $49 · 3XL $51" (recommended prices). */
  const bigSizes = (p: AdminPricingReport["products"][number]) => {
    const rows = p.sizes.filter((s) => s.size !== p.baseSize);
    if (rows.length === 0) return <span className="text-gray-400">—</span>;
    return rows
      .map(
        (s) =>
          `${s.size} ${s.recommendedCents != null ? money(s.recommendedCents) : money(s.priceCents)}`,
      )
      .join(" · ");
  };

  return (
    <div className="rounded-lg border border-accent-light bg-cream/40 p-4 flex flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm text-gray-700">
          <span className="font-semibold text-brown-dark">Pricing report</span>{" "}
          · whole catalog, current price vs cost vs recommended — not affected
          by the date range below
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label htmlFor="pricing-target" className="text-sm text-gray-600">
            Target margin
          </label>
          <select
            id="pricing-target"
            value={target}
            onChange={(e) => changeTarget(Number(e.target.value))}
            className="rounded-md border border-accent-light bg-white px-2 py-1 text-sm"
          >
            {PRICING_TARGETS.map((t) => (
              <option key={t} value={t}>
                {t}%{t === 48 ? " (≈2× cost)" : ""}
              </option>
            ))}
          </select>
          <button
            onClick={view}
            disabled={loading !== null}
            className="btn-primary text-sm disabled:opacity-50"
          >
            {loading === "view"
              ? "Loading…"
              : open
                ? "Hide pricing"
                : "View pricing"}
          </button>
          <button
            onClick={downloadCsv}
            disabled={loading !== null}
            className="btn-secondary text-sm disabled:opacity-50"
            title="One row per product × size: price, cost, margin, recommended"
          >
            {loading === "csv" ? "Building…" : "Download CSV"}
          </button>
        </div>
      </div>

      {!snapshotAvailable && (
        <p className="text-xs text-amber-600">
          Costs haven&apos;t been refreshed yet — run{" "}
          <span className="font-semibold">Refresh margins</span> above first or
          every product will show as uncosted.
        </p>
      )}
      {error && <ErrorNote>{error}</ErrorNote>}

      {open && report && (
        <div className="flex flex-col gap-2 mt-1">
          <p className="text-xs text-gray-600">
            {report.summary.raise} of {report.summary.products} products are
            priced below the {report.targetPct}% target (worst first). Cost,
            Margin and Minimum show a <strong>low–high range</strong> across
            every size and color (a 3XL costs more than an S at the same
            retail). Minimum Recommended = cost ÷ (1 − target) — the exact price
            that hits the target margin; its high end covers every variation.
            Recommended rounds the base up to a clean $2 price point; big sizes
            add +$3 / +$5 / +$7 (2XL / 3XL / 4XL). Prices are never recommended
            down.
            {report.summary.noCost > 0 &&
              ` ${report.summary.noCost} product(s) have no cost on file.`}
            {report.summary.costUnderstated > 0 && (
              <>
                {" "}
                <strong>Places</strong> is how many spots are decorated. Cost
                covers only one of them, so the {report.summary.costUnderstated}{" "}
                product(s) showing a{" "}
                <span className="text-amber-600 font-semibold">+$</span> bump
                cost more than Cost says and their margins read high — hover for
                the breakdown. Those bumps are estimates and are <em>not</em>{" "}
                included in Recommended.
              </>
            )}
          </p>
          <div className="overflow-x-auto rounded-lg border border-accent-light">
            <table className="w-full text-sm">
              <thead className="bg-cream text-left text-gray-600">
                <tr>
                  <th className="px-3 py-2 font-semibold">Product</th>
                  <th
                    className="px-3 py-2 font-semibold text-right"
                    title="Printful blank cost — low–high across every size and color"
                  >
                    Cost
                  </th>
                  <th
                    className="px-3 py-2 font-semibold text-right"
                    title="Decorations on the product (print/embroidery spots). The blank cost only covers one — hover a count for the list and the suggested price bump."
                  >
                    Places
                  </th>
                  <th className="px-3 py-2 font-semibold text-right">
                    Current
                  </th>
                  <th
                    className="px-3 py-2 font-semibold text-right"
                    title="Margin at current prices — worst–best variation"
                  >
                    Margin
                  </th>
                  <th
                    className="px-3 py-2 font-semibold text-right"
                    title={`The exact price this product must sell for to hit the ${report.targetPct}% target margin`}
                  >
                    Minimum Recommended
                  </th>
                  <th className="px-3 py-2 font-semibold text-right">
                    Recommended
                  </th>
                  <th className="px-3 py-2 font-semibold">Big sizes</th>
                </tr>
              </thead>
              <tbody>
                {report.products.map((p) => (
                  <tr
                    key={p.name}
                    className={`border-t border-accent-light ${
                      p.action === "raise" ? "bg-amber-50" : ""
                    }`}
                  >
                    <td className="px-3 py-2">
                      {p.name}
                      {p.uncostedVariations > 0 && p.action !== "no-cost" && (
                        <span
                          className="text-amber-600"
                          title={`${p.uncostedVariations} variation(s) have no cost on file`}
                        >
                          {" "}
                          *
                        </span>
                      )}
                    </td>
                    <td
                      className="px-3 py-2 text-right text-gray-600 whitespace-nowrap"
                      title={
                        p.costLoCents != null &&
                        p.costHiCents != null &&
                        p.costHiCents !== p.costLoCents
                          ? `${money(p.costLoCents)} (base) → ${money(p.costHiCents)} (priciest size/color)`
                          : undefined
                      }
                    >
                      {moneyRange(p.costLoCents, p.costHiCents)}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {placementsCell(p, report.targetPct)}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {money(p.currentCents)}
                    </td>
                    <td
                      className={`px-3 py-2 text-right whitespace-nowrap ${
                        p.marginLoPct == null
                          ? "text-gray-400"
                          : p.marginLoPct < report.targetPct - 5
                            ? "text-rust font-semibold"
                            : p.marginLoPct < report.targetPct
                              ? "text-amber-600"
                              : "text-green-700"
                      }`}
                      title={
                        p.marginLoPct != null
                          ? `Worst variation: ${p.marginLoPct}% · best: ${p.marginHiPct}%`
                          : undefined
                      }
                    >
                      {pctRange(p.marginLoPct, p.marginHiPct)}
                    </td>
                    <td
                      className={`px-3 py-2 text-right whitespace-nowrap ${
                        p.minimumHiCents != null &&
                        p.currentCents < p.minimumHiCents
                          ? "text-rust font-semibold"
                          : "text-gray-600"
                      }`}
                      title={
                        p.minimumCents != null &&
                        p.minimumHiCents != null &&
                        p.minimumHiCents !== p.minimumCents
                          ? `${money(p.minimumCents)} covers the base size; ${money(p.minimumHiCents)} covers every variation`
                          : undefined
                      }
                    >
                      {moneyRange(p.minimumCents, p.minimumHiCents)}
                    </td>
                    <td className="px-3 py-2 text-right">{priceCell(p)}</td>
                    <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                      {bigSizes(p)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-500">
            Cost = cached Printful blank/base price (one print included) —
            embroidered / multi-placement items read optimistically, so treat
            their recommendations as a floor. Apply changes with{" "}
            <code>scripts/update-square-prices.js</code> (dry-run first).
          </p>
        </div>
      )}
    </div>
  );
};

/**
 * Section: Pricing & Costs — the FORWARD-looking half of the economics.
 *
 * Deliberately split from the Dashboard tab: everything here is about what we
 * *should* charge (cost inputs + recommended prices across the whole catalog),
 * with no date range at all. The Dashboard tab is the backward-looking half —
 * what actually sold in a window. The cost snapshot is the seam: it's refreshed
 * here and consumed by the margin figures there.
 */
const PricingCostsSection: React.FC<{ authToken: string }> = ({
  authToken,
}) => {
  const [snap, setSnap] = useState<AdminCostSnapshotMeta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshMsg, setRefreshMsg] = useState<string | null>(null);

  // Snapshot meta only — no order scan (?snapshotOnly=1), so opening this tab
  // is cheap.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await getAdminCostSnapshot(authToken);
      if (cancelled) return;
      if (!res || res.error || !res.data) {
        setError(res?.error ?? "Failed to load cost snapshot.");
      } else {
        setSnap(res.data.costSnapshot);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [authToken]);

  const refreshCosts = async () => {
    setRefreshing(true);
    setRefreshMsg(null);
    const res = await refreshAdminCosts(authToken);
    setRefreshing(false);
    if (res?.retryAfterSeconds != null) {
      // Cooldown, not a failure — the cached costs are still good, so say so
      // plainly rather than implying something broke.
      setRefreshMsg(
        `${res.error} Try again in ${Math.ceil(res.retryAfterSeconds / 60)} min.`,
      );
    } else if (!res || res.error || !res.data) {
      setRefreshMsg(`Cost refresh failed: ${res?.error ?? "unknown error"}`);
    } else {
      setRefreshMsg("Costs refreshed ✓");
      setSnap(res.data.costSnapshot);
    }
  };

  if (loading) return <p className="text-gray-500 text-sm">Loading costs…</p>;
  if (error && !snap) return <ErrorNote>{error}</ErrorNote>;

  const available = snap?.available ?? false;

  return (
    <div className="flex flex-col gap-5">
      <p className="text-sm text-gray-600 lora-text max-w-4xl">
        What we <span className="font-semibold">should</span> charge — cost
        inputs and catalog-wide recommended prices. Nothing here is scoped to a
        date range; for what actually sold, see the{" "}
        <span className="font-semibold">Dashboard</span> tab (whose margin
        figures read the same cost snapshot refreshed below).
      </p>

      {/* Cost snapshot controls — the input every margin and recommendation on
          the site is computed from, here and on the Dashboard tab. */}
      <div className="rounded-lg border border-accent-light bg-cream/40 p-4 flex flex-col gap-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="text-sm text-gray-700">
            <span className="font-semibold text-brown-dark">
              Cost &amp; margin data
            </span>{" "}
            {available ? (
              <>
                · updated {relativeAge(snap?.updatedAt)} ·{" "}
                {snap?.costedVariants ?? 0} costed
                {snap?.uncostedVariants
                  ? `, ${snap.uncostedVariants} uncosted`
                  : ""}
              </>
            ) : (
              <span className="text-gray-500">
                · not refreshed yet — margin unavailable
              </span>
            )}
          </div>
          <button
            onClick={refreshCosts}
            disabled={refreshing}
            className="btn-primary text-sm disabled:opacity-50"
          >
            {refreshing ? "Pulling from Printful… (~15s)" : "Refresh margins"}
          </button>
        </div>
        {refreshMsg && (
          <p
            className={`text-xs ${
              refreshMsg.includes("failed") ? "text-rust" : "text-green-700"
            }`}
          >
            {refreshMsg}
          </p>
        )}
      </div>

      <PricingReportPanel authToken={authToken} snapshotAvailable={available} />
    </div>
  );
};

const SalesDashboardSection: React.FC<{ authToken: string }> = ({
  authToken,
}) => {
  const [data, setData] = useState<AdminSalesSummary | null>(null);
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reporting, setReporting] = useState<"html" | "csv" | null>(null);
  const [reportMsg, setReportMsg] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await getAdminSalesSummary(authToken, days);
    if (!res || res.error || !res.data) {
      setError(res?.error ?? "Failed to load sales summary.");
    } else {
      setData(res.data);
    }
    setLoading(false);
  }, [authToken, days]);

  useEffect(() => {
    load();
  }, [load]);

  // Fetch the COMPLETE per-product breakdown (?full=1 — the dashboard payload is
  // capped at the top 15) for the selected window, then build + download it.
  const downloadReport = async (format: "html" | "csv") => {
    setReporting(format);
    setReportMsg(null);
    const res = await getAdminSalesSummary(authToken, days, true);
    setReporting(null);
    if (!res || res.error || !res.data) {
      setReportMsg(`Report failed: ${res?.error ?? "unknown error"}`);
      return;
    }
    if (format === "html") downloadReportHtml(res.data);
    else downloadReportCsv(res.data);
    setReportMsg("Report downloaded ✓");
  };

  if (loading && !data)
    return <p className="text-gray-500 text-sm">Loading sales…</p>;
  if (error && !data) return <ErrorNote>{error}</ErrorNote>;
  if (!data) return null;

  const snap = data.costSnapshot;
  const hasMargin = snap.available && data.costedUnits > 0;

  return (
    <div className="flex flex-col gap-5">
      <p className="text-sm text-gray-600 lora-text max-w-4xl">
        Sales for the selected window ({data.environment}). Revenue counts{" "}
        <span className="font-semibold">money actually collected</span> — orders
        with a captured payment that isn&apos;t refunded. Abandoned,
        authorized-but-not-captured, voided, and fully-refunded orders are all
        excluded. Partial refunds aren&apos;t deducted. Margin uses the cached
        Printful blank costs — refresh them in the{" "}
        <span className="font-semibold">Pricing &amp; Costs</span> tab.
      </p>

      {!snap.available && (
        <p className="text-xs text-gray-500">
          Costs haven&apos;t been pulled yet, so margin is unavailable — refresh
          them in the <span className="font-semibold">Pricing &amp; Costs</span>{" "}
          tab.
        </p>
      )}
      {snap.available && data.uncostedUnits > 0 && (
        <p className="text-xs text-gray-500">
          {data.uncostedUnits} sold unit(s) had no cost on file, so they&apos;re
          excluded from margin. Embroidered / multi-placement items also read
          optimistically (base blank cost only).
        </p>
      )}

      {/* Sales-window toolbar — the range select scopes everything below it
          (stats, chart, top products) AND the two report downloads, so those
          buttons live in this box rather than floating next to the
          catalog-wide controls above. */}
      <div className="rounded-lg border border-accent-light bg-white p-4 flex flex-col gap-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-brown-dark">
              Sales for range
            </span>
            <select
              id="sales-range"
              aria-label="Sales range"
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="rounded-md border border-accent-light bg-white px-2 py-1 text-sm"
            >
              {RANGE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => downloadReport("html")}
              className="btn-secondary text-sm disabled:opacity-50"
              disabled={reporting !== null}
              title="Full P&L + per-product breakdown for this range, as a printable document"
            >
              {reporting === "html" ? "Building…" : "Download report"}
            </button>
            <button
              onClick={() => downloadReport("csv")}
              className="btn-secondary text-sm disabled:opacity-50"
              disabled={reporting !== null}
              title="Full per-product breakdown for this range as CSV (Excel / Sheets)"
            >
              {reporting === "csv" ? "Building…" : "CSV"}
            </button>
            <button
              onClick={load}
              className="btn-secondary text-sm"
              disabled={loading}
            >
              {loading ? "Loading…" : "Refresh"}
            </button>
          </div>
        </div>
        <p className="text-xs text-gray-500">
          The stats, chart, and downloads below all use this range.
        </p>
        {reportMsg && (
          <p
            className={`text-xs ${
              reportMsg.includes("failed") ? "text-rust" : "text-green-700"
            }`}
          >
            {reportMsg}
          </p>
        )}
      </div>

      {error && <ErrorNote>{error}</ErrorNote>}

      {/* Headline stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <SummaryStat
          label="Revenue (products)"
          value={money(data.productRevenueCents)}
          accent="text-green-700"
        />
        <SummaryStat label="Orders" value={data.orderCount} />
        <SummaryStat label="Avg order value" value={money(data.aovCents)} />
        <SummaryStat
          label="Gross margin"
          value={hasMargin ? money(data.marginCents) : "—"}
          accent={
            hasMargin
              ? data.marginCents >= 0
                ? "text-green-700"
                : "text-rust"
              : "text-gray-400"
          }
        />
      </div>

      {/* Secondary stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <SummaryStat
          label="Collected (incl. tax/ship)"
          value={money(data.totalCollectedCents)}
        />
        <SummaryStat label="Shipping" value={money(data.shippingCents)} />
        <SummaryStat label="Tax" value={money(data.taxCents)} />
        <SummaryStat label="Discounts" value={money(data.discountCents)} />
      </div>

      {data.truncated && (
        <p className="text-xs text-amber-600">
          Showing the most recent orders only — narrow the range to see the full
          window.
        </p>
      )}

      {data.refundedCents > 0 && (
        <p className="text-xs text-gray-500">
          {money(data.refundedCents)} in partial refunds on counted orders isn
          &apos;t deducted from revenue above. Fully-refunded orders are
          excluded entirely.
        </p>
      )}

      {/* Revenue over time */}
      {data.timeseries.length > 1 && (
        <div>
          <h3 className="text-sm font-semibold text-brown-dark mb-2">
            Revenue by day
          </h3>
          <RevenueBars points={data.timeseries} />
        </div>
      )}

      {/* Top products — kept light (qty + revenue). Full per-product cost/margin
          breakdown lives in the downloadable economic report above. */}
      {data.topProducts.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-brown-dark mb-2">
            Top products by revenue
          </h3>
          <div className="overflow-x-auto rounded-lg border border-accent-light">
            <table className="w-full text-sm">
              <thead className="bg-cream text-left text-gray-600">
                <tr>
                  <th className="px-3 py-2 font-semibold">Product</th>
                  <th className="px-3 py-2 font-semibold text-right">Qty</th>
                  <th className="px-3 py-2 font-semibold text-right">
                    Revenue
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.topProducts.map((p) => (
                  <tr key={p.name} className="border-t border-accent-light">
                    <td className="px-3 py-2">{p.name}</td>
                    <td className="px-3 py-2 text-right text-gray-600">
                      {p.quantity}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {money(p.revenueCents)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Per-product cost &amp; margin is in the{" "}
            <span className="font-semibold">Download report</span> above.
          </p>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Section: SKU Sync
// ---------------------------------------------------------------------------

const SkuSyncSection: React.FC<{ authToken: string }> = ({ authToken }) => {
  const [report, setReport] = useState<AdminSkuSyncResult | null>(null);
  // Not auto-run on mount — the SKU check hits both Printful and Square, so it's
  // kicked off explicitly by the "Check SKUs" button below.
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [applied, setApplied] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await getAdminSkuSync(authToken);
    if (!res || res.error || !res.data) {
      setError(res?.error ?? "Failed to load SKU sync report.");
    } else {
      setReport(res.data);
    }
    setLoading(false);
  }, [authToken]);

  const handleApply = async () => {
    if (!report || report.pendingChangeCount === 0) return;
    const ok = window.confirm(
      `Write ${report.pendingChangeCount} corrected SKU(s) to the ${report.environment} Square catalog? This changes live product data.`,
    );
    if (!ok) return;
    setApplying(true);
    setError(null);
    const res = await applyAdminSkuSync(authToken);
    if (!res || res.error || !res.data) {
      setError(res?.error ?? "Failed to apply SKU changes.");
    } else {
      setApplied(res.data.appliedCount);
      setReport(res.data);
    }
    setApplying(false);
  };

  // Before the first run (or after an error with no report yet) show an explicit
  // start button rather than auto-fetching on mount.
  if (!report) {
    return (
      <div className="flex flex-col gap-4">
        <p className="text-sm text-gray-600 lora-text max-w-4xl">
          Each Square variation's SKU suffix must point at the correct Printful
          sync variant so orders fulfill as the right design. Running the check
          compares the live Square catalog against Printful and lists any drift.
        </p>
        {error && <ErrorNote>{error}</ErrorNote>}
        <div>
          <button
            onClick={load}
            disabled={loading}
            className="btn-primary text-sm disabled:opacity-50"
          >
            {loading ? "Checking SKUs…" : "Check SKUs"}
          </button>
        </div>
      </div>
    );
  }

  const { coverage } = report;
  return (
    <div className="flex flex-col gap-5">
      <p className="text-sm text-gray-600 lora-text max-w-4xl">
        Each Square variation's SKU suffix must point at the correct Printful
        sync variant so orders fulfill as the right design. This compares the
        live {report.environment} Square catalog against Printful and lists any
        drift.
      </p>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <SummaryStat
          label="Variations matched"
          value={`${coverage.matched}/${coverage.totalVariations}`}
          accent="text-green-700"
        />
        <SummaryStat
          label="Unmatched (not fulfillable)"
          value={coverage.unmatchedCount}
          accent={coverage.unmatchedCount ? "text-red-600" : "text-brown-dark"}
        />
        <SummaryStat
          label="Printful sync variants"
          value={report.printfulSyncVariants}
        />
        <SummaryStat
          label="SKUs needing update"
          value={report.pendingChangeCount}
          accent={
            report.pendingChangeCount ? "text-amber-600" : "text-green-700"
          }
        />
      </div>

      {applied !== null && (
        <p className="text-green-700 text-sm">
          Applied {applied} SKU change{applied === 1 ? "" : "s"} to Square.
        </p>
      )}
      {error && <ErrorNote>{error}</ErrorNote>}

      <div className="flex items-center gap-3">
        <button
          onClick={handleApply}
          disabled={applying || report.pendingChangeCount === 0}
          className="btn-primary text-sm disabled:opacity-50"
        >
          {applying
            ? "Applying…"
            : report.pendingChangeCount === 0
              ? "All SKUs in sync"
              : `Apply ${report.pendingChangeCount} change${report.pendingChangeCount === 1 ? "" : "s"}`}
        </button>
        <button
          onClick={load}
          disabled={applying || loading}
          className="btn-secondary text-sm disabled:opacity-50"
        >
          {loading ? "Checking…" : "Re-check SKUs"}
        </button>
      </div>

      {report.changes.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-brown-dark mb-2">
            Pending SKU changes
          </h3>
          <div className="overflow-x-auto rounded-lg border border-accent-light">
            <table className="w-full text-sm">
              <thead className="bg-cream text-left text-gray-600">
                <tr>
                  <th className="px-3 py-2 font-semibold">Item</th>
                  <th className="px-3 py-2 font-semibold">Variation</th>
                  <th className="px-3 py-2 font-semibold">Current SKU</th>
                  <th className="px-3 py-2 font-semibold">Corrected SKU</th>
                </tr>
              </thead>
              <tbody>
                {report.changes.map((c, i) => (
                  <tr key={i} className="border-t border-accent-light">
                    <td className="px-3 py-2">{c.itemName}</td>
                    <td className="px-3 py-2 text-gray-600">
                      {c.variationName}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-red-600">
                      {c.oldSku}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-green-700">
                      {c.newSku}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {report.unmatched.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-red-700 mb-2">
            Unmatched variations — no Printful sync variant (orders would void)
          </h3>
          <ul className="flex flex-col gap-1 text-sm text-gray-700">
            {report.unmatched.map((u, i) => (
              <li key={i} className="flex flex-wrap gap-x-2">
                <span className="font-semibold">{u.itemName}</span>
                <span className="text-gray-500">{u.variationName}</span>
                {u.sku && (
                  <span
                    className="font-mono text-xs text-gray-400"
                    translate="no"
                  >
                    ({u.sku})
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {report.warnings.length > 0 && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-3">
          <h3 className="text-sm font-semibold text-amber-800 mb-1">
            Warnings
          </h3>
          <ul className="list-disc list-inside text-xs text-amber-800 flex flex-col gap-1">
            {report.warnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Section: Swatch Sync — repair colour bubbles (Square option-value hexes)
// ---------------------------------------------------------------------------
// The colour bubbles come from Square's hand-entered CatalogItemOptionValue.color
// swatch, which the Printful→Square sync silently WIPES whenever a product/colour
// is added. This re-derives each swatch from the real Printful blank colour and
// (on apply) writes the corrections back. Conflicts (one shared value spanning
// blanks of genuinely different colours) are reported, never auto-fixed.

const SwatchSyncSection: React.FC<{ authToken: string }> = ({ authToken }) => {
  const [report, setReport] = useState<AdminSwatchSyncResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [applied, setApplied] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await getAdminSwatchSync(authToken);
    if (!res || res.error || !res.data) {
      setError(res?.error ?? "Failed to load swatch sync report.");
    } else {
      setReport(res.data);
    }
    setLoading(false);
  }, [authToken]);

  // Colours are read from the cached Printful cost snapshot. If it hasn't been
  // built yet the check returns needsRefresh; refreshing it (the same slow pull
  // the sales dashboard uses) populates the colours, then we re-run the check.
  const handleRefreshData = async () => {
    setRefreshing(true);
    setError(null);
    const res = await refreshAdminCosts(authToken);
    if (!res || res.error) {
      setError(res?.error ?? "Failed to refresh Printful data.");
    } else if (res.retryAfterSeconds) {
      setError(
        `Printful data was refreshed recently — try again in ~${Math.ceil(
          res.retryAfterSeconds / 60,
        )} min.`,
      );
    } else {
      await load();
    }
    setRefreshing(false);
  };

  const handleApply = async () => {
    if (!report?.summary || report.summary.fixable === 0) return;
    const ok = window.confirm(
      `Write ${report.summary.fixable} corrected colour swatch(es) to the ${report.environment} Square catalog? This changes live product data.`,
    );
    if (!ok) return;
    setApplying(true);
    setError(null);
    const res = await applyAdminSwatchSync(authToken);
    if (!res || res.error || !res.data) {
      setError(res?.error ?? "Failed to apply swatch changes.");
    } else {
      setApplied(res.data.appliedCount ?? 0);
      setReport(res.data);
    }
    setApplying(false);
  };

  if (!report) {
    return (
      <div className="flex flex-col gap-4">
        <p className="text-sm text-gray-600 lora-text max-w-4xl">
          Colour bubbles come from Square's swatch hexes, which the Printful
          sync wipes whenever a product or colour is added. Running the check
          compares each swatch against the real Printful blank colour and lists
          any that are wrong or missing.
        </p>
        {error && <ErrorNote>{error}</ErrorNote>}
        <div>
          <button
            onClick={load}
            disabled={loading}
            className="btn-primary text-sm disabled:opacity-50"
          >
            {loading ? "Checking swatches…" : "Check swatches"}
          </button>
        </div>
      </div>
    );
  }

  // The colours come from the cached Printful snapshot; if it isn't populated
  // yet, guide the operator to refresh it (a live crawl here would time out).
  if (report.needsRefresh) {
    return (
      <div className="flex flex-col gap-4">
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">
          {report.message ??
            "Printful colour data isn't cached yet. Refresh it, then re-run the swatch check."}
        </div>
        {error && <ErrorNote>{error}</ErrorNote>}
        <div className="flex items-center gap-3">
          <button
            onClick={handleRefreshData}
            disabled={refreshing || loading}
            className="btn-primary text-sm disabled:opacity-50"
          >
            {refreshing ? "Refreshing Printful data…" : "Refresh Printful data"}
          </button>
          <button
            onClick={load}
            disabled={refreshing || loading}
            className="btn-secondary text-sm disabled:opacity-50"
          >
            {loading ? "Checking…" : "Re-check swatches"}
          </button>
        </div>
        <p className="text-xs text-gray-500 max-w-3xl">
          The check reads blank colours from the cached Printful cost snapshot
          (the same one the sales dashboard uses) so it returns instantly
          instead of crawling Printful live. Refreshing rebuilds that snapshot.
        </p>
      </div>
    );
  }

  const summary = report.summary;
  const coverage = report.coverage;
  if (!summary || !coverage) return null;
  const wrongRows = report.wrong ?? [];
  const missingRows = report.missing ?? [];
  const conflictRows = report.conflicts ?? [];
  const displayOffRows = report.displayOff ?? [];
  return (
    <div className="flex flex-col gap-5">
      <p className="text-sm text-gray-600 lora-text max-w-4xl">
        Each colour swatch is compared against the real Printful blank colour
        (from the cached cost snapshot) on the {report.environment} catalog.
        Wrong/missing swatches are fixed on apply; conflicts (one colour value
        shared across blanks of different colours) are listed for you to split
        in Square.
      </p>

      {/* Square resets the Color option's display to "Text" when it wipes the
          hexes, and the API can't flip it back — nothing here writes that flag.
          The check now reports it per option instead of nagging unconditionally. */}
      {displayOffRows.length > 0 ? (
        <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">
          <p className="font-semibold mb-1">
            Set the option display to “Text and color” in Square
          </p>
          <p className="text-xs mb-2">
            These options have swatch hexes but Square is showing them as plain
            text, so the bubbles stay hidden in the dashboard. Fix by hand in
            Square → Items → Options — applying fixes here cannot set it.
          </p>
          <ul className="flex flex-col gap-1 text-xs">
            {displayOffRows.map((o) => (
              <li key={o.id}>
                <span className="font-semibold">{o.name}</span> —{" "}
                {o.valuesWithHex} of {o.totalValues} values have a hex
                {!o.audited && (
                  <span className="text-red-700">
                    {" "}
                    · name doesn't match “color” either, so this option's
                    swatches are NOT being checked above
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p className="text-xs text-gray-500">
          Option display is set to “Text and color” everywhere swatches are
          used.
        </p>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <SummaryStat
          label="Swatches correct"
          value={summary.correct}
          accent="text-green-700"
        />
        <SummaryStat
          label="Wrong / missing"
          value={summary.wrong + summary.missing}
          accent={summary.fixable ? "text-amber-600" : "text-green-700"}
        />
        <SummaryStat
          label="Conflicts (manual)"
          value={summary.conflicting}
          accent={summary.conflicting ? "text-red-600" : "text-brown-dark"}
        />
        <SummaryStat
          label="Variations joined"
          value={`${coverage.mappedVariations}`}
        />
      </div>

      {applied !== null && (
        <p className="text-green-700 text-sm">
          Applied {applied} swatch fix{applied === 1 ? "" : "es"} to Square.
        </p>
      )}
      {error && <ErrorNote>{error}</ErrorNote>}

      <div className="flex items-center gap-3">
        <button
          onClick={handleApply}
          disabled={applying || summary.fixable === 0}
          className="btn-primary text-sm disabled:opacity-50"
        >
          {applying
            ? "Applying…"
            : summary.fixable === 0
              ? "All swatches correct"
              : `Apply ${summary.fixable} fix${summary.fixable === 1 ? "" : "es"}`}
        </button>
        <button
          onClick={load}
          disabled={applying || loading}
          className="btn-secondary text-sm disabled:opacity-50"
        >
          {loading ? "Checking…" : "Re-check swatches"}
        </button>
      </div>

      {summary.wrong + summary.missing > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-brown-dark mb-2">
            Swatches to fix
          </h3>
          <div className="overflow-x-auto rounded-lg border border-accent-light">
            <table className="w-full text-sm">
              <thead className="bg-cream text-left text-gray-600">
                <tr>
                  <th className="px-3 py-2 font-semibold">Colour</th>
                  <th className="px-3 py-2 font-semibold">Current</th>
                  <th className="px-3 py-2 font-semibold">Corrected</th>
                  <th className="px-3 py-2 font-semibold">Blank</th>
                </tr>
              </thead>
              <tbody>
                {[...wrongRows, ...missingRows].map((r, i) => (
                  <tr key={i} className="border-t border-accent-light">
                    <td className="px-3 py-2 font-semibold">{r.label}</td>
                    <td className="px-3 py-2">
                      <span className="inline-flex items-center gap-1.5">
                        <span
                          className="inline-block h-4 w-4 rounded-full border border-gray-300"
                          style={{
                            backgroundColor: r.current ?? "transparent",
                          }}
                        />
                        <span className="font-mono text-xs text-red-600">
                          {r.current ?? "—"}
                        </span>
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <span className="inline-flex items-center gap-1.5">
                        <span
                          className="inline-block h-4 w-4 rounded-full border border-gray-300"
                          style={{
                            backgroundColor: r.correct ?? "transparent",
                          }}
                        />
                        <span className="font-mono text-xs text-green-700">
                          {r.correct ?? "—"}
                        </span>
                      </span>
                    </td>
                    <td className="px-3 py-2 text-gray-500 text-xs">
                      {r.pfName}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {conflictRows.length > 0 && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3">
          <h3 className="text-sm font-semibold text-red-700 mb-1">
            Conflicts — fix manually in Square
          </h3>
          <p className="text-xs text-red-800 mb-2">
            One shared colour value maps to different real colours across
            blanks; no single swatch is right for all. Split the value in Square
            (e.g. “Navy (Comfort Colors)”) or accept the closest.
          </p>
          <ul className="flex flex-col gap-2 text-sm text-gray-700">
            {conflictRows.map((c, i) => (
              <li key={i}>
                <span className="font-semibold">{c.label}</span>{" "}
                <span className="text-gray-500">
                  (current {c.current ?? "—"})
                </span>
                <ul className="ml-4 mt-0.5 flex flex-col gap-0.5 text-xs text-gray-600">
                  {c.options.map((o, j) => (
                    <li key={j} className="flex items-center gap-1.5">
                      <span
                        className="inline-block h-3 w-3 rounded-full border border-gray-300"
                        style={{ backgroundColor: o.hex }}
                      />
                      <span className="font-mono">{o.hex}</span>
                      <span>
                        {o.name} ×{o.count} — {o.where.join(", ")}
                      </span>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Section: Site Controls — checkout kill switch + instant catalog cache bust
// ---------------------------------------------------------------------------

const SiteControlsSection: React.FC<{ authToken: string }> = ({
  authToken,
}) => {
  const [config, setConfig] = useState<AdminSiteConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const { bustLocalCatalogCache } = useCatalog();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await getAdminSiteConfig(authToken);
    if (!res || res.error || !res.data) {
      setError(res?.error ?? "Failed to load site controls.");
    } else {
      setConfig(res.data);
    }
    setLoading(false);
  }, [authToken]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleCheckout = async () => {
    if (!config) return;
    const turningOff = config.checkoutEnabled;
    const ok = window.confirm(
      turningOff
        ? "Turn checkout OFF for all shoppers? They can still browse and build a cart, but the pay button is disabled until you turn it back on."
        : "Turn checkout back ON for all shoppers?",
    );
    if (!ok) return;
    setBusy(true);
    setError(null);
    setNote(null);
    const res = await setAdminCheckoutEnabled(
      authToken,
      !config.checkoutEnabled,
    );
    if (!res || res.error || !res.data) {
      setError(res?.error ?? "Failed to update checkout setting.");
    } else {
      setConfig(res.data);
      setNote(
        `Checkout is now ${res.data.checkoutEnabled ? "ON" : "OFF"}. Visitors pick it up on their next page load.`,
      );
    }
    setBusy(false);
  };

  const bustCache = async () => {
    const ok = window.confirm(
      "Refresh the catalog cache for ALL visitors? Everyone drops their cached products, prices, and colour swatches and refetches on their next page load. Use this after a price, product, or swatch change.",
    );
    if (!ok) return;
    setBusy(true);
    setError(null);
    setNote(null);
    const res = await bumpAdminCatalogEpoch(authToken);
    if (!res || res.error || !res.data) {
      setError(res?.error ?? "Failed to refresh catalog cache.");
    } else {
      setConfig(res.data);
      // Drop THIS tab's caches too. The bump only reaches a tab through
      // get-site-config, so without this the admin tab keeps the pre-bump
      // catalog in memory — and, sharing localStorage with every other tab on
      // the domain, used to write it straight back over the fresh copy.
      bustLocalCatalogCache(res.data.catalogEpoch);
      setNote(
        `Catalog cache refreshed (epoch ${res.data.catalogEpoch}). Every visitor refetches on their next load; this tab has already dropped its own copy.`,
      );
    }
    setBusy(false);
  };

  if (loading) {
    return <p className="text-sm text-gray-500">Loading site controls…</p>;
  }
  if (!config) {
    return (
      <div className="flex flex-col gap-3">
        {error && <ErrorNote>{error}</ErrorNote>}
        <div>
          <button onClick={load} className="btn-secondary text-sm">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      {error && <ErrorNote>{error}</ErrorNote>}
      {note && <p className="text-green-700 text-sm">{note}</p>}

      {/* Checkout kill switch */}
      <div className="rounded-lg border border-accent-light p-4 flex flex-col gap-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold text-brown-dark">Checkout</h3>
            <p className="text-xs text-gray-500 max-w-xl">
              Turn public checkout off during an incident. Browsing and carts
              keep working; only the pay button is disabled, with a banner.
              Takes effect on each visitor's next page load. No deploy needed.
            </p>
          </div>
          <span
            className={`shrink-0 rounded-full px-3 py-1 text-xs font-semibold ${
              config.checkoutEnabled
                ? "bg-green-100 text-green-800"
                : "bg-red-100 text-red-800"
            }`}
          >
            {config.checkoutEnabled ? "ON" : "OFF"}
          </span>
        </div>
        <div>
          <button
            onClick={toggleCheckout}
            disabled={busy}
            className={`text-sm text-white rounded-lg px-4 py-2 disabled:opacity-50 ${
              config.checkoutEnabled
                ? "bg-red-600 hover:bg-red-700"
                : "bg-green-600 hover:bg-green-700"
            }`}
          >
            {busy
              ? "Saving…"
              : config.checkoutEnabled
                ? "Turn checkout OFF"
                : "Turn checkout ON"}
          </button>
        </div>
      </div>

      {/* Instant cache bust */}
      <div className="rounded-lg border border-accent-light p-4 flex flex-col gap-3">
        <div>
          <h3 className="text-sm font-semibold text-brown-dark">
            Refresh catalog cache for everyone
          </h3>
          <p className="text-xs text-gray-500 max-w-xl">
            Visitors cache products, prices, and colour swatches in their
            browser for up to a few hours. Bumping the cache makes every visitor
            refetch on their next page load — use it right after a price,
            product, or swatch change so people see it immediately.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={bustCache}
            disabled={busy}
            className="btn-primary text-sm disabled:opacity-50"
          >
            {busy ? "Refreshing…" : "Refresh cache for all visitors"}
          </button>
          <span className="text-xs text-gray-400">
            Cache epoch {config.catalogEpoch}
            {config.updatedAt
              ? ` · updated ${new Date(config.updatedAt).toLocaleString()}`
              : ""}
          </span>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Section: Site Visibility — hide/unhide products on the storefront
// ---------------------------------------------------------------------------

const SiteVisibilitySection: React.FC<{ authToken: string }> = ({
  authToken,
}) => {
  const [items, setItems] = useState<AdminCatalogItem[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  // Ids with an in-flight toggle — disables just that row's button.
  const [pending, setPending] = useState<Set<string>>(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await getCatalogVisibility(authToken);
    if (!res || res.error || !res.data) {
      setError(res?.error ?? "Failed to load catalog.");
    } else {
      setItems(res.data.items);
    }
    setLoading(false);
  }, [authToken]);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = async (item: AdminCatalogItem) => {
    const nextHidden = !item.hidden;
    setPending((p) => new Set(p).add(item.id));
    // Optimistic flip.
    setItems(
      (prev) =>
        prev?.map((i) =>
          i.id === item.id ? { ...i, hidden: nextHidden } : i,
        ) ?? prev,
    );
    const res = await setItemHidden(authToken, item.id, nextHidden);
    if (!res || res.error || !res.data) {
      // Roll back on failure.
      setItems(
        (prev) =>
          prev?.map((i) =>
            i.id === item.id ? { ...i, hidden: item.hidden } : i,
          ) ?? prev,
      );
      setError(res?.error ?? "Failed to update visibility.");
    }
    setPending((p) => {
      const n = new Set(p);
      n.delete(item.id);
      return n;
    });
  };

  if (loading) return <p className="text-gray-500 text-sm">Loading catalog…</p>;
  if (error && !items) return <ErrorNote>{error}</ErrorNote>;
  if (!items) return null;

  const hiddenCount = items.filter((i) => i.hidden).length;
  const q = query.trim().toLowerCase();
  const shown = q
    ? items.filter((i) => i.name.toLowerCase().includes(q))
    : items;

  return (
    <div className="flex flex-col gap-5">
      <div className="rounded-lg border border-accent-light bg-cream/40 p-3 text-sm text-gray-600 lora-text">
        Hide a product to remove it from the storefront (search, categories,
        related products, and its page) without deleting it in Square. This is
        the store's own control — Square's dashboard "Site visibility" toggle
        does not apply to this site. Visitors with a cached catalog see the
        change within ~2 hours; new visitors immediately.
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <SummaryStat label="Products" value={items.length} />
        <SummaryStat
          label="Hidden"
          value={hiddenCount}
          accent={hiddenCount ? "text-rust" : "text-brown-dark"}
        />
        <SummaryStat label="Visible" value={items.length - hiddenCount} />
      </div>

      <div className="flex flex-col sm:flex-row gap-2 sm:items-center justify-between">
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search products…"
          className="w-full sm:max-w-xs rounded-lg border border-accent-light px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-brown"
        />
        <button onClick={load} className="btn-secondary text-sm">
          Refresh
        </button>
      </div>

      {error && <ErrorNote>{error}</ErrorNote>}

      <div className="flex flex-col divide-y divide-accent-light rounded-lg border border-accent-light bg-white overflow-hidden">
        {shown.length === 0 && (
          <p className="p-4 text-sm text-gray-500">No matching products.</p>
        )}
        {shown.map((item) => (
          <div
            key={item.id}
            className="flex items-center justify-between gap-3 px-3 py-2.5"
          >
            <div className="min-w-0 flex items-center gap-2">
              <span
                className={`text-sm truncate ${
                  item.hidden ? "text-gray-400 line-through" : "text-brown-dark"
                }`}
              >
                {item.name}
              </span>
              {item.hidden && (
                <span className="flex-shrink-0 rounded-full bg-rust/10 text-rust px-2 py-0.5 text-[10px] font-semibold">
                  Hidden
                </span>
              )}
            </div>
            <button
              onClick={() => toggle(item)}
              disabled={pending.has(item.id)}
              className="btn-secondary text-xs disabled:opacity-50 flex-shrink-0"
            >
              {pending.has(item.id) ? "Saving…" : item.hidden ? "Show" : "Hide"}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Section: Homepage Groups — which products sit in the "Classics" carousel
//
// The homepage shows two carousels: Classics (this curated list) and Designs
// (every other visible product, automatically) — plus "Neither", an explicit
// opt-out list for products that shouldn't be showcased on the homepage at all
// (they stay on sale, in the shop and in search; only the homepage skips them). Stored as Square ITEM ids in
// the site-config Blobs store, NOT as a Square category — a category is what a
// product's canonical URL is built from, so grouping by category would rewrite
// those URLs and orphan the indexed ones. The product list is borrowed from
// admin-catalog-visibility (it's the one endpoint that already lists every
// Square item by id + name).
// ---------------------------------------------------------------------------

const HOMEPAGE_GROUPS = [
  { id: "classics", label: "Classics" },
  { id: "designs", label: "Designs" },
  { id: "none", label: "Neither" },
] as const;

type HomepageGroup = (typeof HOMEPAGE_GROUPS)[number]["id"];

const HomepageGroupsSection: React.FC<{ authToken: string }> = ({
  authToken,
}) => {
  const [items, setItems] = useState<AdminCatalogItem[] | null>(null);
  const [classicIds, setClassicIds] = useState<Set<string>>(new Set());
  const [excludedIds, setExcludedIds] = useState<Set<string>>(new Set());
  // The last SAVED sets — let the panel show unsaved changes and revert.
  const [savedIds, setSavedIds] = useState<Set<string>>(new Set());
  const [savedExcludedIds, setSavedExcludedIds] = useState<Set<string>>(
    new Set(),
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setNote(null);
    const [catalog, config] = await Promise.all([
      getCatalogVisibility(authToken),
      getAdminSiteConfig(authToken),
    ]);
    const loadedItems =
      catalog && !catalog.error && catalog.data ? catalog.data.items : null;
    if (!loadedItems) {
      setError(catalog?.error ?? "Failed to load catalog.");
    } else {
      setItems(loadedItems);
    }
    if (!config || config.error || !config.data) {
      setError(config?.error ?? "Failed to load homepage groups.");
    } else {
      const saved = new Set(config.data.classicItemIds ?? []);
      const savedExcluded = new Set(config.data.homepageExcludedItemIds ?? []);
      setSavedIds(new Set(saved));
      setSavedExcludedIds(new Set(savedExcluded));
      // Saved ids with no product behind them (deleted, or replaced — a rebuilt
      // product gets a NEW Square item id) have no row to untick, so they'd
      // otherwise be re-saved forever and inflate the counts. Stage their
      // removal: the panel comes up dirty and one Save cleans the config.
      // Only when the catalog actually loaded — a failed fetch must not look
      // like "every product was deleted".
      const known = loadedItems ? new Set(loadedItems.map((i) => i.id)) : null;
      const prune = (ids: Set<string>) =>
        known
          ? new Set(Array.from(ids).filter((id) => known.has(id)))
          : new Set(ids);
      const live = prune(saved);
      const liveExcluded = prune(savedExcluded);
      setClassicIds(live);
      setExcludedIds(liveExcluded);
      const stale =
        saved.size - live.size + (savedExcluded.size - liveExcluded.size);
      if (stale > 0) {
        setNote(
          `${stale} saved entr${stale === 1 ? "y is" : "ies are"} for ${stale === 1 ? "a product" : "products"} that no longer exist${stale === 1 ? "s" : ""} — the homepage already ignores ${stale === 1 ? "it" : "them"}. Press Save groups to clear ${stale === 1 ? "it" : "them"}.`,
        );
      }
    }
    setLoading(false);
  }, [authToken]);

  useEffect(() => {
    load();
  }, [load]);

  const groupOf = (id: string): HomepageGroup =>
    excludedIds.has(id) ? "none" : classicIds.has(id) ? "classics" : "designs";

  // Assign one product to a group. Two stored lists, and "Designs" is still
  // the remainder of neither — so a newly added product lands on the homepage
  // by default instead of silently vanishing. "Neither" is the explicit
  // opt-out, and the two lists are kept disjoint here as well as server-side.
  const setGroup = (id: string, group: HomepageGroup) => {
    setNote(null);
    setClassicIds((prev) => {
      const want = group === "classics";
      if (prev.has(id) === want) return prev;
      const next = new Set(prev);
      if (want) next.add(id);
      else next.delete(id);
      return next;
    });
    setExcludedIds((prev) => {
      const want = group === "none";
      if (prev.has(id) === want) return prev;
      const next = new Set(prev);
      if (want) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const differs = (a: Set<string>, b: Set<string>) =>
    a.size !== b.size || Array.from(a).some((id) => !b.has(id));

  const dirty =
    differs(classicIds, savedIds) || differs(excludedIds, savedExcludedIds);

  const save = async () => {
    setSaving(true);
    setError(null);
    setNote(null);
    // Whole-list replace — the server treats these as the complete groups. Send
    // only ids that exist in the catalog we're showing: a saved id for a
    // product that's since been deleted or rebuilt (a replaced product gets a
    // NEW Square item id) has no row here to untick, so without this filter
    // every save would faithfully write it back forever — invisible on the
    // storefront, which drops unknown ids, but inflating the count here.
    const known = new Set((items ?? []).map((i) => i.id));
    const kept = Array.from(classicIds).filter((id) => known.has(id));
    const keptExcluded = Array.from(excludedIds).filter((id) => known.has(id));
    const dropped =
      classicIds.size - kept.length + (excludedIds.size - keptExcluded.length);
    const res = await setAdminHomepageGroups(authToken, kept, keptExcluded);
    if (!res || res.error || !res.data) {
      setError(res?.error ?? "Failed to save homepage groups.");
    } else {
      const ids = new Set(res.data.classicItemIds ?? []);
      const excluded = new Set(res.data.homepageExcludedItemIds ?? []);
      setClassicIds(ids);
      setSavedIds(new Set(ids));
      setExcludedIds(excluded);
      setSavedExcludedIds(new Set(excluded));
      const staleNote =
        dropped > 0
          ? ` Removed ${dropped} leftover entr${dropped === 1 ? "y" : "ies"} for products that no longer exist.`
          : "";
      const excludedNote = excluded.size
        ? ` ${excluded.size} product${excluded.size === 1 ? " is" : "s are"} off the homepage (still on sale in the shop).`
        : "";
      setNote(
        ids.size === 0
          ? `Saved. With no Classics chosen the homepage shows one combined carousel again. Visitors see it on their next page load.${excludedNote}${staleNote}`
          : `Saved — ${ids.size} product${ids.size === 1 ? "" : "s"} in Classics. Visitors see it on their next page load.${excludedNote}${staleNote}`,
      );
    }
    setSaving(false);
  };

  if (loading) return <p className="text-gray-500 text-sm">Loading catalog…</p>;
  if (error && !items) return <ErrorNote>{error}</ErrorNote>;
  if (!items) return null;

  const excludedCount = items.filter((i) => excludedIds.has(i.id)).length;
  const classicCount = items.filter(
    (i) => !excludedIds.has(i.id) && classicIds.has(i.id),
  ).length;
  const designsCount = items.length - excludedCount - classicCount;

  return (
    <div className="flex flex-col gap-5">
      <div className="rounded-lg border border-accent-light bg-cream/40 p-3 text-sm text-gray-600 lora-text">
        The homepage shows two carousels:{" "}
        <span className="font-semibold">Classics</span> and{" "}
        <span className="font-semibold">Designs</span>. Choose a group for each
        product below; anything left on Designs stays there automatically, so a
        newly added product is never missing from the homepage. Side by side on
        a computer, stacked with Classics on top on a phone. If nothing is in
        Classics, the homepage shows one combined carousel. Pick{" "}
        <span className="font-semibold">Neither</span> to keep a product off the
        homepage altogether — it stays on sale and in the shop, it just is not
        showcased. This is a homepage layout choice only — it does{" "}
        <strong>not</strong> change product URLs, categories, or search
        listings. Takes effect on each visitor&apos;s next page load; no deploy
        needed.
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <SummaryStat label="Products" value={items.length} />
        <SummaryStat
          label="Classics"
          value={classicCount}
          accent={classicCount ? "text-rust" : "text-brown-dark"}
        />
        <SummaryStat label="Designs" value={designsCount} />
        <SummaryStat
          label="Neither"
          value={excludedCount}
          accent={excludedCount ? "text-rust" : "text-brown-dark"}
        />
      </div>

      {error && <ErrorNote>{error}</ErrorNote>}
      {note && <p className="text-green-700 text-sm">{note}</p>}

      <div className="flex flex-col divide-y divide-accent-light rounded-lg border border-accent-light bg-white overflow-hidden">
        {items.map((item) => {
          const current = groupOf(item.id);
          return (
            <div
              key={item.id}
              className="flex items-center justify-between gap-3 px-3 py-2.5 hover:bg-cream/40"
            >
              <span className="min-w-0 flex items-center gap-2">
                <span className="text-sm truncate">{item.name}</span>
                {item.hidden && (
                  <span className="flex-shrink-0 rounded-full bg-gray-100 text-gray-500 px-2 py-0.5 text-[10px] font-semibold">
                    Hidden
                  </span>
                )}
              </span>
              {/* Three-state segmented control. Radios rather than buttons so
                  arrow keys move within the group and screen readers announce
                  "1 of 3" per product. */}
              <span
                role="radiogroup"
                aria-label={`Homepage group for ${item.name}`}
                className="flex-shrink-0 flex rounded-full border border-accent-light overflow-hidden text-xs"
              >
                {HOMEPAGE_GROUPS.map(({ id, label }) => {
                  const selected = current === id;
                  return (
                    <label
                      key={id}
                      className={`cursor-pointer px-3 py-1 font-semibold transition-colors ${
                        selected
                          ? "bg-brown-dark text-cream"
                          : "text-gray-500 hover:bg-cream/60"
                      }`}
                    >
                      <input
                        type="radio"
                        className="sr-only"
                        name={`group-${item.id}`}
                        checked={selected}
                        onChange={() => setGroup(item.id, id)}
                      />
                      {label}
                    </label>
                  );
                })}
              </span>
            </div>
          );
        })}
      </div>

      <div className="flex items-center gap-3">
        <button
          onClick={save}
          disabled={saving || !dirty}
          className="btn-primary text-sm disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save groups"}
        </button>
        <button
          onClick={() => {
            setClassicIds(new Set(savedIds));
            setExcludedIds(new Set(savedExcludedIds));
            setNote(null);
          }}
          disabled={saving || !dirty}
          className="btn-secondary text-sm disabled:opacity-50"
        >
          Revert
        </button>
        {dirty && <span className="text-xs text-rust">Unsaved changes</span>}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Section: Printful Catalog health
// ---------------------------------------------------------------------------

const PrintfulCatalogSection: React.FC<{ authToken: string }> = ({
  authToken,
}) => {
  const [data, setData] = useState<AdminPrintfulCatalog | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await getAdminPrintfulCatalog(authToken);
    if (!res || res.error || !res.data) {
      setError(res?.error ?? "Failed to load Printful catalog.");
    } else {
      setData(res.data);
    }
    setLoading(false);
  }, [authToken]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading)
    return <p className="text-gray-500 text-sm">Loading Printful catalog…</p>;
  if (error) return <ErrorNote>{error}</ErrorNote>;
  if (!data) return null;

  const s = data.summary;
  const allGood = s.discontinuing + s.discontinued + s.outOfStock === 0;

  return (
    <div className="flex flex-col gap-5">
      <div
        className={`rounded-lg border p-3 text-sm ${
          allGood
            ? "bg-green-50 border-green-200 text-green-800"
            : "bg-amber-50 border-amber-200 text-amber-800"
        }`}
      >
        {allGood
          ? `All ${s.totalProducts} Printful products are in good standing.`
          : `${s.discontinued} discontinued, ${s.discontinuing} discontinuing, ${s.outOfStock} out of stock — review below.`}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <SummaryStat label="Products" value={s.totalProducts} />
        <SummaryStat
          label="Good standing"
          value={s.inGoodStanding}
          accent="text-green-700"
        />
        <SummaryStat
          label="Discontinuing"
          value={s.discontinuing}
          accent="text-amber-600"
        />
        <SummaryStat
          label="Discontinued"
          value={s.discontinued}
          accent="text-red-600"
        />
        <SummaryStat
          label="Out of stock"
          value={s.outOfStock}
          accent="text-orange-600"
        />
        <SummaryStat
          label="Unlinked variants"
          value={s.unlinkedVariants}
          accent={s.unlinkedVariants ? "text-red-600" : "text-brown-dark"}
        />
      </div>

      <div className="flex justify-end">
        <button onClick={load} className="btn-secondary text-sm">
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {data.products.map((p) => {
          const open = expanded === p.id;
          return (
            <div
              key={p.id ?? p.name}
              className="rounded-lg border border-accent-light bg-white overflow-hidden flex flex-col"
            >
              <button
                onClick={() => setExpanded(open ? null : p.id)}
                className="text-left p-3 flex gap-3 items-center hover:bg-cream transition-colors"
              >
                {p.thumbnail ? (
                  <img
                    src={p.thumbnail}
                    alt=""
                    className="h-12 w-12 rounded object-cover flex-shrink-0 bg-gray-100"
                  />
                ) : (
                  <div className="h-12 w-12 rounded bg-gray-100 flex-shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-brown-dark truncate">
                    {p.name}
                  </p>
                  <p className="text-xs text-gray-500">
                    {p.variantCount} variant{p.variantCount === 1 ? "" : "s"} ·{" "}
                    {p.linkedVariantCount}/{p.variantCount} linked to Square
                  </p>
                  <div className="mt-1">
                    <StatusChip status={p.status} />
                  </div>
                </div>
              </button>

              {open && (
                <div className="border-t border-accent-light bg-cream/40 p-3 flex flex-col gap-2">
                  {p.variants.map((v) => (
                    <div
                      key={v.id ?? v.name}
                      className="flex items-center justify-between gap-2 text-xs"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-gray-700">{v.name}</p>
                        <p className="font-mono text-[10px] text-gray-400 truncate">
                          <span translate="no">{v.sku ?? "no SKU"}</span>
                          {!v.linkedToSquare && (
                            <span className="text-red-500">
                              {" "}
                              · not linked to Square
                            </span>
                          )}
                        </p>
                      </div>
                      <StatusChip status={v.status} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Section: Feed Health (Netlify Blobs — feed-health, written by the daily canary)
//
// Reports what check-feed-health.mjs last saw when it fetched /meta-feed.csv and
// /sitemap.xml. It does NOT re-probe on load: the canary is the single source of
// truth, so this tab and the alert email can never tell different stories about
// the same morning.
//
// The stale banner is the most important thing on the page. A monitor that has
// stopped running looks identical to one reporting all-clear, and that is the
// exact shape of the failure this whole feature exists to catch — so a report
// older than 36 hours is rendered as an alarm, not a footnote.
// ---------------------------------------------------------------------------

/** Coarse "how long ago" from milliseconds. */
const sinceLabel = (ms: number | null): string => {
  if (ms === null || !Number.isFinite(ms)) return "never";
  const min = Math.floor(ms / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min} min ago`;
  const h = Math.floor(min / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.floor(h / 24)} days ago`;
};

const FeedTargetCard: React.FC<{ target: AdminFeedTarget }> = ({ target }) => (
  <div
    className={`rounded-lg border p-4 ${
      target.healthy
        ? "border-accent-light bg-white"
        : "border-red-300 bg-red-50"
    }`}
  >
    <div className="flex flex-wrap items-center gap-2 mb-2">
      <span className="text-lg font-bold text-brown-dark">{target.name}</span>
      <span
        className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
          target.healthy
            ? "bg-green-100 text-green-800"
            : "bg-red-100 text-red-800"
        }`}
      >
        {target.healthy ? "Serving correctly" : "Problem"}
      </span>
      {/* Proves feed-routes.js answered rather than the SPA fallback quietly
          reclaiming the path — the precise August 2026 regression. */}
      {!target.servedByEdge && (
        <span className="rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-xs font-semibold">
          Not served by the edge function
        </span>
      )}
    </div>

    <p className="text-xs text-gray-500 opensans-text mb-3">
      <a
        href={target.url}
        target="_blank"
        rel="noreferrer"
        className="underline hover:text-brown break-all"
      >
        {target.url}
      </a>
      {target.consumer ? ` · pulled by ${target.consumer}` : ""}
    </p>

    {target.problems.length > 0 && (
      <ul className="mb-3 list-disc pl-5 text-sm text-red-800">
        {target.problems.map((p) => (
          <li key={p}>{p}</li>
        ))}
      </ul>
    )}

    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
      <div>
        <p className="text-xs text-gray-500">HTTP</p>
        <p className="font-semibold text-brown-dark">
          {target.status || "no response"}
        </p>
      </div>
      <div>
        <p className="text-xs text-gray-500">Content type</p>
        <p className="font-mono text-xs text-brown-dark break-all">
          {target.contentType || "—"}
        </p>
      </div>
      <div>
        <p className="text-xs text-gray-500 capitalize">{target.unit}</p>
        <p className="font-semibold text-brown-dark">
          {target.count}
          {target.previousCount !== null && (
            <span className="ml-1 text-xs font-normal text-gray-500">
              (was {target.previousCount})
            </span>
          )}
        </p>
      </div>
      <div>
        <p className="text-xs text-gray-500">Response</p>
        <p className="font-semibold text-brown-dark">{target.ms} ms</p>
      </div>
    </div>
  </div>
);

const FeedHealthSection: React.FC<{ authToken: string }> = ({ authToken }) => {
  const [data, setData] = useState<AdminFeedHealth | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await getAdminFeedHealth(authToken);
    if (!res || res.error || !res.data) {
      setError(res?.error ?? "Failed to load feed health.");
    } else {
      setData(res.data);
    }
    setLoading(false);
  }, [authToken]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading)
    return <p className="text-gray-500 text-sm">Loading feed health…</p>;
  if (error) return <ErrorNote>{error}</ErrorNote>;
  if (!data) return null;

  const neverRun = data.checkedAt === null;

  return (
    <div className="flex flex-col gap-5">
      <p className="text-sm text-gray-600 lora-text max-w-4xl">
        Meta Commerce Manager and Google pull{" "}
        <span className="font-mono text-xs">/meta-feed.csv</span> and{" "}
        <span className="font-mono text-xs">/sitemap.xml</span> on their own
        schedules. A broken feed is invisible from the site itself — the URL
        still answers 200, nothing throws — so a canary fetches both every
        morning and checks what a 200 doesn't: the content type, the shape of
        the payload, and whether the row count has collapsed. Meta pauses its
        scheduled feed after 7 consecutive failures.
      </p>

      {!data.blobsAvailable && (
        <div className="rounded-lg bg-gray-50 border border-gray-200 p-3 text-sm text-gray-600">
          Netlify Blobs aren't available in this environment, so no report could
          be read. (Expected under plain <code>npm start</code>; use{" "}
          <code>netlify dev</code> or the deployed site.)
        </div>
      )}

      {/* Staleness outranks the check results: if the canary stopped running,
          the checks below are history, not status. */}
      {data.blobsAvailable && data.stale && (
        <div className="rounded-lg border border-red-300 bg-red-50 p-4">
          <p className="font-semibold text-red-800">
            {neverRun
              ? "The feed canary has never completed a run."
              : `The feed canary hasn't run since ${sinceLabel(data.ageMs)}.`}
          </p>
          <p className="text-sm text-red-800 mt-1">
            It's scheduled daily at 11:00 UTC. Everything below is the last
            report it managed to write — treat it as history, not current
            status. Check the function logs for{" "}
            <span className="font-mono text-xs">check-feed-health</span>.
          </p>
        </div>
      )}

      {data.blobsAvailable && !data.stale && (
        <div
          className={`rounded-lg border p-4 ${
            data.healthy
              ? "border-green-300 bg-green-50"
              : "border-red-300 bg-red-50"
          }`}
        >
          <p
            className={`font-semibold ${
              data.healthy ? "text-green-800" : "text-red-800"
            }`}
          >
            {data.healthy
              ? "Both endpoints are serving correctly."
              : "An endpoint is not serving correctly."}
          </p>
          <p className="text-sm text-gray-600 mt-1">
            Last checked {sinceLabel(data.ageMs)}
            {data.checkedAt
              ? ` (${new Date(data.checkedAt).toLocaleString()})`
              : ""}
            {data.base ? ` · ${data.base}` : ""}
          </p>
        </div>
      )}

      <div className="flex justify-end">
        <button onClick={load} className="btn-secondary text-sm">
          Refresh
        </button>
      </div>

      {data.targets.length > 0 && (
        <div className="flex flex-col gap-3">
          {data.targets.map((t) => (
            <FeedTargetCard key={t.name} target={t} />
          ))}
        </div>
      )}

      {data.history.length > 1 && (
        <div className="rounded-lg border border-accent-light bg-white p-4">
          <h3 className="text-sm font-semibold text-brown-dark mb-3">
            Recent runs
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
                  <th className="pb-2 pr-4 font-semibold">Checked</th>
                  <th className="pb-2 pr-4 font-semibold">Result</th>
                  {Object.keys(data.history[0].counts).map((name) => (
                    <th key={name} className="pb-2 pr-4 font-semibold">
                      {name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.history.map((h) => (
                  <tr
                    key={h.checkedAt}
                    className="border-t border-accent-light"
                  >
                    <td className="py-2 pr-4 text-gray-600">
                      {new Date(h.checkedAt).toLocaleString()}
                    </td>
                    <td className="py-2 pr-4">
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                          h.healthy
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {h.healthy ? "OK" : "Problem"}
                      </span>
                    </td>
                    {Object.keys(data.history[0].counts).map((name) => (
                      <td
                        key={name}
                        className="py-2 pr-4 font-semibold text-brown-dark"
                      >
                        {h.counts[name] ?? "—"}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Section: Tax Blobs (Netlify Blobs — nexus tracking)
// ---------------------------------------------------------------------------

const TaxBlobsSection: React.FC<{ authToken: string }> = ({ authToken }) => {
  const [data, setData] = useState<AdminTaxBlobs | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await getAdminTaxBlobs(authToken);
    if (!res || res.error || !res.data) {
      setError(res?.error ?? "Failed to load tax data.");
    } else {
      setData(res.data);
    }
    setLoading(false);
  }, [authToken]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading)
    return <p className="text-gray-500 text-sm">Loading tax blobs…</p>;
  if (error) return <ErrorNote>{error}</ErrorNote>;
  if (!data) return null;

  return (
    <div className="flex flex-col gap-5">
      <p className="text-sm text-gray-600 lora-text max-w-4xl">
        Cumulative per-state sales tracked in the Netlify Blobs{" "}
        <span className="font-mono text-xs">{data.storeName}</span> store. An
        alert email fires the first time a state crosses{" "}
        {(data.thresholds.alertFraction * 100).toFixed(0)}% of either
        economic-nexus threshold ({money(data.thresholds.salesThresholdCents)}{" "}
        in sales or {data.thresholds.transactionThreshold} transactions).
      </p>

      {!data.blobsAvailable && (
        <div className="rounded-lg bg-gray-50 border border-gray-200 p-3 text-sm text-gray-600">
          Netlify Blobs aren't available in this environment, so no totals could
          be read. (This is expected under plain <code>npm start</code>; use{" "}
          <code>netlify dev</code> or the deployed site.)
        </div>
      )}

      <div className="flex justify-end">
        <button onClick={load} className="btn-secondary text-sm">
          Refresh
        </button>
      </div>

      {data.states.length === 0 ? (
        <div className="rounded-lg border border-accent-light bg-white p-6 text-center text-gray-500 text-sm">
          No sales have been tracked yet. States appear here after their first
          captured order.
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {data.states.map((st) => (
            <div
              key={st.state}
              className="rounded-lg border border-accent-light bg-white p-4"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-lg font-bold text-brown-dark">
                    {st.state}
                  </span>
                  {st.hasNexus && (
                    <span className="rounded-full bg-blue-100 text-blue-800 px-2 py-0.5 text-xs font-semibold">
                      Physical nexus
                    </span>
                  )}
                  {st.alertSent && (
                    <span className="rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-xs font-semibold">
                      Alert sent
                    </span>
                  )}
                  {st.nearThreshold && !st.hasNexus && (
                    <span className="rounded-full bg-red-100 text-red-800 px-2 py-0.5 text-xs font-semibold">
                      Near threshold
                    </span>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <div className="flex justify-between text-xs text-gray-600 mb-1">
                    <span>Sales</span>
                    <span className="font-semibold">
                      {money(st.salesTotalCents)} /{" "}
                      {money(data.thresholds.salesThresholdCents)}
                    </span>
                  </div>
                  <ProgressBar
                    value={st.salesProgress}
                    color={st.salesProgress >= 0.8 ? "bg-red-500" : "bg-brown"}
                  />
                </div>
                <div>
                  <div className="flex justify-between text-xs text-gray-600 mb-1">
                    <span>Transactions</span>
                    <span className="font-semibold">
                      {st.transactionCount} /{" "}
                      {data.thresholds.transactionThreshold}
                    </span>
                  </div>
                  <ProgressBar
                    value={st.txProgress}
                    color={st.txProgress >= 0.8 ? "bg-red-500" : "bg-brown"}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Section: Open Orders — recent (pre-capture) + in-flight + buyer lookup
// ---------------------------------------------------------------------------

/** Human relative age from minutes-ago. */
const ago = (min: number): string => {
  if (min < 1) return "just now";
  if (min < 60) return `${min} min ago`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m ? `${h}h ${m}m ago` : `${h}h ago`;
};

const addr = (a: AdminOrderDetail["shippingAddress"]): string =>
  [
    a.line1,
    a.line2,
    [a.city, a.state].filter(Boolean).join(", "),
    a.postalCode,
    a.country && a.country !== "US" ? a.country : null,
  ]
    .filter(Boolean)
    .join(" · ");

/** Expandable detail panel: fetched lazily when a row is opened. */
const OrderDetailPanel: React.FC<{ authToken: string; orderId: string }> = ({
  authToken,
  orderId,
}) => {
  const [detail, setDetail] = useState<AdminOrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      const res = await getAdminOrderDetail(authToken, orderId);
      if (!alive) return;
      if (!res || res.error || !res.data) {
        setError(res?.error ?? "Failed to load detail.");
      } else {
        setDetail(res.data);
      }
      setLoading(false);
    })();
    return () => {
      alive = false;
    };
  }, [authToken, orderId]);

  if (loading)
    return <p className="text-xs text-gray-500 px-1 py-2">Loading detail…</p>;
  if (error) return <ErrorNote>{error}</ErrorNote>;
  if (!detail) return null;

  return (
    <div className="border-t border-accent-light bg-cream/40 p-3 flex flex-col gap-3 text-xs">
      <div>
        <p className="font-semibold text-brown-dark mb-1">Items</p>
        <table className="w-full">
          <tbody>
            {detail.lineItems.map((li, i) => (
              <tr key={i} className="border-b border-accent-light/60">
                <td className="py-1 pr-2">
                  {li.name}
                  {li.variationName ? (
                    <span className="text-gray-500"> · {li.variationName}</span>
                  ) : null}
                  <span className="text-gray-500"> ×{li.quantity}</span>
                </td>
                <td className="py-1 text-right whitespace-nowrap">
                  {money(li.totalCents)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="flex justify-between mt-1 text-gray-600">
          <span>Tax {money(detail.taxCents)}</span>
          {detail.discountCents > 0 && (
            <span>Discount −{money(detail.discountCents)}</span>
          )}
          {detail.serviceChargeCents > 0 && (
            <span>Shipping {money(detail.serviceChargeCents)}</span>
          )}
          <span className="font-semibold text-brown-dark">
            Total {money(detail.totalCents)}
          </span>
        </div>
      </div>

      <div>
        <p className="font-semibold text-brown-dark mb-1">Ship to</p>
        <p className="text-gray-700">{addr(detail.shippingAddress) || "—"}</p>
        {detail.phone && <p className="text-gray-500">{detail.phone}</p>}
      </div>

      {detail.tracking && (
        <div>
          <p className="font-semibold text-brown-dark mb-1">Tracking</p>
          <p className="text-gray-700" translate="no">
            {detail.tracking.carrier ? `${detail.tracking.carrier} · ` : ""}
            {detail.tracking.trackingNumber}
          </p>
          {detail.tracking.trackingUrl && (
            <a
              href={detail.tracking.trackingUrl}
              target="_blank"
              rel="noreferrer"
              className="text-brown underline"
            >
              Track package →
            </a>
          )}
        </div>
      )}
    </div>
  );
};

const OrderRow: React.FC<{
  authToken: string;
  order: AdminOpenOrder;
  onVoid: (order: AdminOpenOrder) => void;
  onRefund: (order: AdminOpenOrder) => void;
  busyId: string | null;
}> = ({ authToken, order, onVoid, onRefund, busyId }) => {
  const [open, setOpen] = useState(false);
  const [resending, setResending] = useState<string | null>(null);
  const [resendMsg, setResendMsg] = useState<string | null>(null);
  const busy = busyId === order.orderId;

  const resend = async (type: "confirmation" | "shipped") => {
    setResending(type);
    setResendMsg(null);
    const res = await resendAdminEmail(authToken, order.orderId, type);
    setResending(null);
    setResendMsg(
      res?.error || !res?.data
        ? `${type} email failed: ${res?.error ?? "unknown error"}`
        : `${type === "confirmation" ? "Confirmation" : "Shipped"} email sent ✓`,
    );
  };

  return (
    <div className="rounded-lg border border-accent-light bg-white overflow-hidden">
      <div className="p-4 flex flex-col gap-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-brown-dark">
              {order.customerName ?? "Unknown buyer"}
            </p>
            <p className="text-xs text-gray-500 break-all">
              {order.email ?? "no email"}
            </p>
            <p
              className="font-mono text-[11px] text-gray-400 break-all mt-0.5"
              translate="no"
            >
              {order.orderId}
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm font-bold text-brown-dark">
              {money(order.totalCents)}
            </p>
            <p className="text-xs text-gray-500">{ago(order.ageMinutes)}</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {order.orderState && order.orderState !== "OPEN" && (
            <span
              className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                order.orderState === "CANCELED"
                  ? "bg-red-100 text-red-800"
                  : order.orderState === "COMPLETED"
                    ? "bg-green-100 text-green-800"
                    : "bg-gray-100 text-gray-600"
              }`}
            >
              {order.orderState}
            </span>
          )}
          {order.refundedCents > 0 ? (
            <span className="rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-xs font-semibold">
              Refunded · {money(order.refundedCents)}
            </span>
          ) : order.paymentState === "authorized" ? (
            <span className="rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-xs font-semibold">
              Authorized · not captured
            </span>
          ) : order.paymentState === "captured" ? (
            <span className="rounded-full bg-green-100 text-green-800 px-2 py-0.5 text-xs font-semibold">
              {order.inFlight ? "Paid · awaiting shipment" : "Captured"}
            </span>
          ) : (
            <span className="rounded-full bg-gray-100 text-gray-600 px-2 py-0.5 text-xs font-semibold">
              {order.paymentState}
            </span>
          )}
          {order.paymentState === "authorized" &&
            (order.withinWindow ? (
              <span className="rounded-full bg-blue-100 text-blue-800 px-2 py-0.5 text-xs font-semibold">
                ~{order.minutesUntilCapture} min until capture
              </span>
            ) : (
              <span className="rounded-full bg-orange-100 text-orange-800 px-2 py-0.5 text-xs font-semibold">
                Capturing on next run
              </span>
            ))}
          <span
            className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
              order.hasPrintfulDraft
                ? "bg-gray-100 text-gray-600"
                : order.printfulStatus
                  ? "bg-purple-100 text-purple-800"
                  : "bg-red-100 text-red-800"
            }`}
          >
            {order.hasPrintfulDraft
              ? "Printful draft"
              : order.printfulStatus
                ? `Printful: ${order.printfulStatus}`
                : "No Printful order"}
          </span>
        </div>

        {resendMsg && (
          <p
            className={`text-xs ${
              resendMsg.includes("failed") ? "text-rust" : "text-green-700"
            }`}
          >
            {resendMsg}
          </p>
        )}

        <div className="flex flex-wrap items-center justify-between gap-2">
          <button
            onClick={() => setOpen((v) => !v)}
            className="text-xs text-brown underline"
          >
            {open ? "Hide details" : "View details"}
          </button>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => resend("confirmation")}
              disabled={!!resending}
              className="btn-secondary text-xs disabled:opacity-50"
            >
              {resending === "confirmation"
                ? "Sending…"
                : "Resend confirmation"}
            </button>
            <button
              onClick={() => resend("shipped")}
              disabled={!!resending}
              className="btn-secondary text-xs disabled:opacity-50"
              title={
                order.hasTracking
                  ? "Includes tracking"
                  : "No tracking yet — sends the shipped notice without a tracking number"
              }
            >
              {resending === "shipped" ? "Sending…" : "Resend shipped"}
            </button>
            {order.cancelable && (
              <button
                onClick={() => onVoid(order)}
                disabled={busy}
                className="btn-secondary text-xs disabled:opacity-50"
              >
                {busy ? "Working…" : "Cancel (free void)"}
              </button>
            )}
            {order.refundable && (
              <button
                onClick={() => onRefund(order)}
                disabled={busy}
                className="text-xs font-semibold text-rust border border-rust/40 rounded px-2 py-1 disabled:opacity-50"
              >
                {busy ? "Working…" : "Refund"}
              </button>
            )}
          </div>
        </div>
      </div>

      {open && (
        <OrderDetailPanel authToken={authToken} orderId={order.orderId} />
      )}
    </div>
  );
};

// Four stage lists + a global customer lookup. The lookup is its own tab (not a
// filter layered on a list) so its any-status results — including canceled and
// refunded orders — never get mistaken for the contents of a stage list.
type OrdersView = "recent" | "inflight" | "shipped" | "cancelled" | "lookup";

const ORDERS_VIEWS: OrdersView[] = [
  "recent",
  "inflight",
  "shipped",
  "cancelled",
  "lookup",
];

// Client-side page size for the historical lists.
const ORDERS_PAGE_SIZE = 25;

// Time-window options for the historical lists (value = days; 0 = all time).
const DAY_OPTIONS: { value: number; label: string }[] = [
  { value: 1, label: "Last 24 hours" },
  { value: 7, label: "Last 7 days" },
  { value: 30, label: "Last 30 days" },
  { value: 90, label: "Last 90 days" },
  { value: 0, label: "All time" },
];

// Which views expose the time-window selector + pagination.
const isWindowedView = (v: OrdersView): boolean =>
  v === "inflight" || v === "shipped" || v === "cancelled";

const OpenOrdersSection: React.FC<{ authToken: string }> = ({ authToken }) => {
  const [view, setView] = useTabParam<OrdersView>(
    ADMIN_SUBTAB_PARAM,
    ORDERS_VIEWS,
    "recent",
  );
  const [data, setData] = useState<AdminOpenOrders | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  // Time window (days; 0 = all) + client-side page index for windowed lists.
  const [days, setDays] = useState(1);
  const [page, setPage] = useState(0);

  // Buyer lookup
  const [emailQ, setEmailQ] = useState("");
  const [nameQ, setNameQ] = useState("");
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState<AdminOpenOrders | null>(null);

  // Confirmation modals
  const [pendingVoid, setPendingVoid] = useState<AdminOpenOrder | null>(null);
  const [pendingRefund, setPendingRefund] = useState<AdminOpenOrder | null>(
    null,
  );

  const load = useCallback(async () => {
    // The lookup tab has no list to prefetch — it populates only on an explicit
    // search — so there's nothing to load here for it.
    if (view === "lookup") {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const res =
      view === "inflight"
        ? await getAdminInFlightOrders(authToken, days)
        : view === "shipped"
          ? await getAdminShippedOrders(authToken, days)
          : view === "cancelled"
            ? await getAdminCancelledOrders(authToken, days)
            : await getAdminOpenOrders(authToken);
    if (!res || res.error || !res.data) {
      setError(res?.error ?? "Failed to load orders.");
    } else {
      setData(res.data);
    }
    setLoading(false);
  }, [authToken, view, days]);

  useEffect(() => {
    load();
  }, [load]);

  // Reset to the first page whenever the view, window, or search results change.
  useEffect(() => {
    setPage(0);
  }, [view, days, results]);

  // On the lookup tab the active list is the search results; elsewhere it's the
  // loaded stage list.
  const active = view === "lookup" ? results : data;

  const runSearch = useCallback(async () => {
    if (!emailQ.trim() && !nameQ.trim()) return;
    setSearching(true);
    setError(null);
    const res = await searchAdminOrders(authToken, {
      email: emailQ,
      name: nameQ,
    });
    if (!res || res.error || !res.data) {
      setError(res?.error ?? "Search failed.");
    } else {
      setResults(res.data);
    }
    setSearching(false);
  }, [authToken, emailQ, nameQ]);

  const clearSearch = () => {
    setResults(null);
    setEmailQ("");
    setNameQ("");
    setError(null);
  };

  const refreshActive = useCallback(async () => {
    if (view === "lookup") await runSearch();
    else await load();
  }, [view, runSearch, load]);

  // Auto-refresh was removed (owner request, 2026-07-23) — the Orders tabs load
  // once on open and otherwise only when Refresh is clicked. Polling made the
  // list shift under the cursor mid-action.

  const confirmVoid = async () => {
    if (!pendingVoid) return;
    const orderId = pendingVoid.orderId;
    setPendingVoid(null);
    setBusyId(orderId);
    setError(null);
    const res = await cancelAdminOpenOrder(authToken, orderId);
    setBusyId(null);
    if (!res || res.error || !res.data) {
      setError(res?.error ?? "Failed to void order.");
      return;
    }
    await refreshActive();
  };

  const confirmRefund = async () => {
    if (!pendingRefund) return;
    const orderId = pendingRefund.orderId;
    setPendingRefund(null);
    setBusyId(orderId);
    setError(null);
    const res = await refundAdminOrder(authToken, orderId);
    setBusyId(null);
    if (!res || res.error || !res.data) {
      setError(res?.error ?? "Failed to refund order.");
      return;
    }
    await refreshActive();
  };

  // Client-side pagination over the active list (the server caps at 200/window).
  const allRows = active?.orders ?? [];
  const totalPages = Math.max(1, Math.ceil(allRows.length / ORDERS_PAGE_SIZE));
  const clampedPage = Math.min(page, totalPages - 1);
  const pageStart = clampedPage * ORDERS_PAGE_SIZE;
  const pageRows = allRows.slice(pageStart, pageStart + ORDERS_PAGE_SIZE);

  return (
    <div className="flex flex-col gap-5">
      {/* Tab bar — underline style, stage lists then the global lookup.
          No overflow container: `overflow-x-auto` forces overflow-y to auto,
          and the -mb-px that seats the underlines on the border overflowed by
          1px, rendering a stray vertical scrollbar. Tabs wrap instead. */}
      <div className="-mx-1 border-b border-accent-light">
        <div className="flex flex-wrap gap-1 px-1">
          {(
            [
              { id: "recent", label: "Uncaptured" },
              { id: "inflight", label: "In flight" },
              { id: "shipped", label: "Shipped" },
              { id: "cancelled", label: "Cancelled" },
              { id: "lookup", label: "Customer lookup" },
            ] as { id: OrdersView; label: string }[]
          ).map((v) => (
            <button
              key={v.id}
              onClick={() => {
                setError(null);
                if (v.id !== "lookup") setResults(null);
                setView(v.id);
              }}
              className={`relative -mb-px whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-semibold transition-colors ${
                view === v.id
                  ? "border-brown text-brown-dark"
                  : "border-transparent text-gray-500 hover:border-accent-light hover:text-brown-dark"
              }`}
            >
              {v.label}
            </button>
          ))}
        </div>
      </div>

      <p className="text-sm text-gray-600 lora-text max-w-4xl">
        {view === "recent" ? (
          <>
            Orders placed in the last 15 minutes that aren't captured yet.
            During the {data?.windowMinutes ?? 10}-minute free-cancellation
            window the card is only{" "}
            <span className="font-semibold">authorized</span> — these don't
            appear in the Square dashboard until they capture. Void an
            authorized order to release the hold with no charge and no Square
            fee.
          </>
        ) : view === "inflight" ? (
          <>
            Orders that are{" "}
            <span className="font-semibold">paid but not yet shipped</span> —
            captured and waiting on Printful fulfillment. Refund here (while
            it's not yet in production) or resend a confirmation.
          </>
        ) : view === "shipped" ? (
          <>
            Orders that have <span className="font-semibold">shipped</span> —
            Printful fulfilled them and the shipment is marked complete (the
            same set Square shows under Shipments → Shipped). Read-only here;
            resend the confirmation or shipped/tracking email if a customer
            needs it.
          </>
        ) : view === "cancelled" ? (
          <>
            Orders that were{" "}
            <span className="font-semibold">canceled or refunded</span> — voided
            before capture, refunded after, or canceled by an admin. Terminal
            and read-only (no void/refund actions here). Also catches any legacy
            order that was refunded but left open.
          </>
        ) : (
          <>
            Search <span className="font-semibold">all</span> of a customer's
            orders by email or name — <span className="font-semibold">any</span>{" "}
            status, including canceled and refunded. This is a global lookup,
            separate from the stage lists above, so what you see here isn't
            limited to a single stage.
          </>
        )}
      </p>

      {/* Customer lookup — only on its own tab */}
      {view === "lookup" && (
        <div className="rounded-lg border border-accent-light bg-cream/40 p-4 flex flex-col gap-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input
              type="email"
              value={emailQ}
              onChange={(e) => setEmailQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && runSearch()}
              placeholder="Email (exact)"
              className="rounded-md border border-accent-light px-3 py-2 text-sm"
            />
            <input
              type="text"
              value={nameQ}
              onChange={(e) => setNameQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && runSearch()}
              placeholder="Name (first + last)"
              className="rounded-md border border-accent-light px-3 py-2 text-sm"
            />
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={runSearch}
              disabled={searching || (!emailQ.trim() && !nameQ.trim())}
              className="btn-primary text-sm disabled:opacity-50"
            >
              {searching ? "Searching…" : "Search"}
            </button>
            {results && (
              <button
                onClick={clearSearch}
                className="btn-secondary text-sm"
                disabled={searching}
              >
                Clear results
              </button>
            )}
          </div>
        </div>
      )}

      {/* Time-window selector — historical lists only */}
      {isWindowedView(view) && (
        <div className="flex items-center gap-2">
          <label htmlFor="orders-time-range" className="text-sm text-gray-600">
            Time range
          </label>
          <select
            id="orders-time-range"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="rounded-md border border-accent-light bg-white px-2 py-1 text-sm"
          >
            {DAY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      )}

      {error && <ErrorNote>{error}</ErrorNote>}

      {/* Count + refresh — on lookup, only once a search has run */}
      {(view !== "lookup" || results) && (
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600">
            {view === "lookup"
              ? `${results?.count ?? 0} matching order${
                  (results?.count ?? 0) === 1 ? "" : "s"
                }${results?.truncated ? " (showing first 50)" : ""}`
              : allRows.length === 0
                ? "0 orders"
                : allRows.length > ORDERS_PAGE_SIZE
                  ? `Showing ${pageStart + 1}–${pageStart + pageRows.length} of ${
                      allRows.length
                    }${data?.truncated ? "+" : ""}`
                  : `${allRows.length} order${allRows.length === 1 ? "" : "s"}${
                      data?.truncated ? "+" : ""
                    }`}{" "}
            ({active?.environment})
          </span>
          <button
            onClick={refreshActive}
            className="btn-secondary text-sm"
            disabled={loading || searching}
          >
            Refresh
          </button>
        </div>
      )}

      {view === "lookup" && !results && !searching ? (
        <div className="rounded-lg border border-accent-light bg-white p-6 text-center text-gray-500 text-sm">
          Search by email or name to see all of a customer's orders.
        </div>
      ) : (view === "lookup" ? searching : loading) ? (
        <p className="text-gray-500 text-sm">
          {view === "lookup" ? "Searching…" : "Loading orders…"}
        </p>
      ) : !active || active.orders.length === 0 ? (
        <div className="rounded-lg border border-accent-light bg-white p-6 text-center text-gray-500 text-sm">
          {view === "lookup"
            ? "No orders found for that email or name."
            : view === "inflight"
              ? "No paid, unshipped orders in this window."
              : view === "shipped"
                ? "No shipped orders in this window."
                : view === "cancelled"
                  ? "No canceled or refunded orders in this window."
                  : "No open orders in the last 15 minutes."}
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {pageRows.map((o) => (
            <OrderRow
              key={o.orderId}
              authToken={authToken}
              order={o}
              onVoid={setPendingVoid}
              onRefund={setPendingRefund}
              busyId={busyId}
            />
          ))}
          {allRows.length > ORDERS_PAGE_SIZE && (
            <div className="flex items-center justify-center gap-4 pt-2 text-sm">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={clampedPage === 0}
                className="btn-secondary text-sm disabled:opacity-40"
              >
                ← Prev
              </button>
              <span className="text-gray-600">
                Page {clampedPage + 1} of {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={clampedPage >= totalPages - 1}
                className="btn-secondary text-sm disabled:opacity-40"
              >
                Next →
              </button>
            </div>
          )}
        </div>
      )}

      <ConfirmationModal
        isOpen={!!pendingVoid}
        onConfirm={confirmVoid}
        onCancel={() => setPendingVoid(null)}
      >
        {pendingVoid && (
          <div className="text-left">
            <p className="font-semibold mb-2">Void this order?</p>
            <p className="text-sm mb-3">
              {pendingVoid.customerName ?? "Unknown buyer"} ·{" "}
              {pendingVoid.email ?? "no email"} ·{" "}
              {money(pendingVoid.totalCents)}
            </p>
            <p className="text-sm">
              The card authorization is released (no charge, no Square fee) and
              the Printful draft is deleted. This can't be undone.
            </p>
          </div>
        )}
      </ConfirmationModal>

      <ConfirmationModal
        isOpen={!!pendingRefund}
        onConfirm={confirmRefund}
        onCancel={() => setPendingRefund(null)}
      >
        {pendingRefund && (
          <div className="text-left">
            <p className="font-semibold mb-2 text-rust">Refund this order?</p>
            <p className="text-sm mb-3">
              {pendingRefund.customerName ?? "Unknown buyer"} ·{" "}
              {pendingRefund.email ?? "no email"} ·{" "}
              {money(pendingRefund.totalCents)}
            </p>
            <p className="text-sm">
              This{" "}
              <span className="font-semibold">refunds the full amount</span> to
              the customer, cancels the Printful order, cancels the Square
              order, and emails the customer. This moves real money and can't be
              undone.
            </p>
          </div>
        )}
      </ConfirmationModal>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Section: Needs Attention (durable admin-alert feed)
// ---------------------------------------------------------------------------

const NeedsAttentionSection: React.FC<{ authToken: string }> = ({
  authToken,
}) => {
  const [feed, setFeed] = useState<AdminAlertsFeed | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDismissed, setShowDismissed] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await getAdminAlerts(authToken);
    if (!res || res.error || !res.data) {
      setError(res?.error ?? "Failed to load alerts.");
    } else {
      setFeed(res.data);
    }
    setLoading(false);
  }, [authToken]);

  useEffect(() => {
    load();
  }, [load]);

  const dismiss = async (id: string) => {
    setBusy(true);
    await dismissAdminAlert(authToken, id);
    setBusy(false);
    await load();
  };

  const clearAll = async () => {
    if (!window.confirm("Clear the entire alert feed? This can't be undone."))
      return;
    setBusy(true);
    await clearAdminAlerts(authToken);
    setBusy(false);
    await load();
  };

  if (loading) return <p className="text-gray-500 text-sm">Loading alerts…</p>;
  if (error) return <ErrorNote>{error}</ErrorNote>;
  if (!feed) return null;

  const shown = feed.alerts.filter((a) => showDismissed || !a.dismissed);

  return (
    <div className="flex flex-col gap-5">
      <p className="text-sm text-gray-600 lora-text max-w-4xl">
        System failures that need a human — captured off every admin alert
        (Printful confirm/cancel failures after capture, refund failures,
        finalize errors). Also emailed to support@americanaandcompany.com; this
        is the durable log so nothing gets lost in the inbox.
      </p>

      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-600">
          {feed.blobsAvailable
            ? `${feed.activeCount} active · ${feed.count} total`
            : "counts unavailable"}
        </span>
        <div className="flex items-center gap-3">
          <label className="text-xs text-gray-500 flex items-center gap-1">
            <input
              type="checkbox"
              checked={showDismissed}
              onChange={(e) => setShowDismissed(e.target.checked)}
            />
            Show dismissed
          </label>
          <button onClick={load} className="btn-secondary text-sm">
            Refresh
          </button>
          {feed.count > 0 && (
            <button
              onClick={clearAll}
              disabled={busy}
              className="text-xs font-semibold text-rust border border-rust/40 rounded px-2 py-1 disabled:opacity-50"
            >
              Clear all
            </button>
          )}
        </div>
      </div>

      {!feed.blobsAvailable ? (
        // An unreadable feed is NOT an empty one. Showing the all-clear here
        // would hide the backlog during exactly the outage that produced it.
        // The alerts themselves are still emailed to support@ — say so.
        <div className="rounded-lg border border-rust/40 bg-rust/5 p-6 text-sm text-brown-dark">
          <p className="font-semibold">Alert feed unavailable.</p>
          <p className="mt-1 text-gray-700">
            The durable log couldn't be read, so this list may be incomplete —
            treat it as unknown, not empty. Alerts are still being emailed to
            support@americanaandcompany.com; check there. (Expected under plain{" "}
            <code>npm start</code>; use <code>netlify dev</code>.)
          </p>
        </div>
      ) : shown.length === 0 ? (
        <div className="rounded-lg border border-accent-light bg-white p-6 text-center text-gray-500 text-sm">
          Nothing needs attention. 🎉
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {shown.map((a) => (
            <div
              key={a.id}
              className={`rounded-lg border p-4 flex flex-col gap-2 ${
                a.dismissed
                  ? "border-accent-light bg-gray-50 opacity-70"
                  : "border-rust/30 bg-rust/5"
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-semibold text-brown-dark">
                  {a.subject}
                  {(a.count ?? 1) > 1 && (
                    <span
                      className="ml-2 align-middle rounded-full bg-rust/15 text-rust text-[11px] font-bold px-2 py-0.5"
                      title={
                        a.firstSeen
                          ? `First seen ${new Date(a.firstSeen).toLocaleString()}`
                          : undefined
                      }
                    >
                      ×{a.count}
                    </span>
                  )}
                </p>
                <span className="text-[11px] text-gray-400 whitespace-nowrap">
                  {new Date(a.at).toLocaleString()}
                </span>
              </div>
              <p className="text-sm text-gray-700">{a.message}</p>
              {(a.count ?? 1) > 1 &&
                a.firstSeen &&
                (() => {
                  // "still failing" was asserted for ANY multi-occurrence alert,
                  // so an issue that fired a few times then self-resolved kept
                  // claiming it was ongoing days later. Anchor the claim to the
                  // MOST RECENT occurrence (lastSeen, else `at` which floats to
                  // the top on each repeat): only call it active if it recurred
                  // within the last hour; otherwise just state when it last fired.
                  const lastSeen = a.lastSeen ?? a.at;
                  const stillActive =
                    Date.now() - new Date(lastSeen).getTime() < 60 * 60 * 1000;
                  return (
                    <p className="text-[11px] text-gray-500">
                      Recurring since {new Date(a.firstSeen).toLocaleString()}
                      {stillActive ? (
                        <span className="font-semibold text-rust">
                          {" "}
                          — still failing (last seen{" "}
                          {new Date(lastSeen).toLocaleString()}).
                        </span>
                      ) : (
                        <> — last seen {new Date(lastSeen).toLocaleString()}.</>
                      )}
                    </p>
                  );
                })()}
              {a.context && Object.keys(a.context).length > 0 && (
                <div className="text-xs text-gray-500 flex flex-col gap-0.5">
                  {Object.entries(a.context).map(([k, v]) => (
                    <div key={k} className="break-all">
                      <span className="font-semibold">{k}:</span> {String(v)}
                    </div>
                  ))}
                </div>
              )}
              {!a.dismissed && (
                <div className="flex justify-end">
                  <button
                    onClick={() => dismiss(a.id)}
                    disabled={busy}
                    className="btn-secondary text-xs disabled:opacity-50"
                  >
                    Mark handled
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Section: Return Requests
// ---------------------------------------------------------------------------

const REASON_LABELS: Record<string, string> = {
  defective: "Damaged / defective",
  wrong_item: "Wrong item received",
  not_as_described: "Not as described",
  changed_mind: "Changed mind",
  other: "Other",
};

const RETURN_STATUS_META: Record<
  AdminReturnRequest["status"],
  { label: string; chip: string }
> = {
  requested: { label: "Pending review", chip: "bg-amber-100 text-amber-800" },
  approved: {
    label: "Approved — awaiting item",
    chip: "bg-blue-100 text-blue-800",
  },
  received: { label: "Item received", chip: "bg-purple-100 text-purple-800" },
  denied: { label: "Denied", chip: "bg-red-100 text-red-800" },
  refunded: { label: "Refunded", chip: "bg-green-100 text-green-800" },
  canceled: { label: "Canceled", chip: "bg-gray-100 text-gray-600" },
};

const ReturnRequestRow: React.FC<{
  req: AdminReturnRequest;
  authToken: string;
  onUpdated: () => void;
}> = ({ req, authToken, onUpdated }) => {
  const [open, setOpen] = useState(false);
  const [adminNote, setAdminNote] = useState(req.adminNote);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [confirmRefund, setConfirmRefund] = useState(false);
  const meta = RETURN_STATUS_META[req.status] ?? RETURN_STATUS_META.requested;

  // Suggested refund = sum of the returned items' line values (excl. tax/shipping).
  const suggestedCents = req.items.reduce((s, i) => s + (i.cents ?? 0), 0);
  const [refundDollars, setRefundDollars] = useState(
    suggestedCents > 0 ? (suggestedCents / 100).toFixed(2) : "",
  );

  // The flow is intentionally flexible (steps aren't gated), but refunding
  // before the item is marked "received" skips the normal path — so we confirm.
  const refundSkipsSteps = req.status !== "received";

  const onRefundClick = () => {
    if (refundSkipsSteps) setConfirmRefund(true);
    else act("refund");
  };

  const act = async (
    action: "approve" | "deny" | "refund" | "receive" | "cancel",
  ) => {
    let refundAmount: number | undefined;
    if (action === "refund") {
      const cents = Math.round(parseFloat(refundDollars) * 100);
      if (!Number.isFinite(cents) || cents <= 0) {
        setMsg("Failed: enter a valid refund amount");
        return;
      }
      refundAmount = cents;
    }
    setBusy(true);
    setMsg(null);
    const res = await updateAdminReturnStatus(
      authToken,
      req.id,
      action,
      adminNote,
      refundAmount,
    );
    setBusy(false);
    if (!res || res.error || !res.data) {
      setMsg(`Failed: ${res?.error ?? "unknown error"}`);
    } else {
      setMsg(`Marked as ${ACTION_LABELS[action]} ✓`);
      onUpdated();
    }
  };

  const ACTION_LABELS = {
    approve: "approved",
    deny: "denied",
    refund: "refunded",
    receive: "received",
    cancel: "canceled",
  };

  return (
    <div className="rounded-lg border border-accent-light bg-white overflow-hidden">
      <div className="p-4 flex flex-col gap-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-brown-dark">
              {req.customerName || "Unknown customer"}
            </p>
            <p className="text-xs text-gray-500 break-all">
              {req.customerEmail || "no email"}
            </p>
            <p className="text-xs text-gray-400 font-mono break-all mt-0.5">
              Order: <span translate="no">{req.originalOrderId}</span>
            </p>
          </div>
          <div className="flex flex-col items-end gap-1">
            <span
              className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${meta.chip}`}
            >
              {meta.label}
            </span>
            <span className="text-[11px] text-gray-400">
              {new Date(req.createdAt).toLocaleString()}
            </span>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 text-xs">
          <span className="rounded-full bg-gray-100 text-gray-700 px-2.5 py-0.5 font-semibold">
            {REASON_LABELS[req.reason] ?? req.reason}
          </span>
          {req.items.map((item, i) => (
            <span
              key={i}
              className="rounded-full bg-cream text-brown-dark px-2.5 py-0.5"
            >
              {item.name} &times; {item.quantity}
            </span>
          ))}
        </div>

        {req.notes && (
          <p className="text-xs text-gray-600 italic">
            &ldquo;{req.notes}&rdquo;
          </p>
        )}

        {msg && (
          <p
            className={`text-xs ${
              msg.startsWith("Failed") ? "text-rust" : "text-green-700"
            }`}
          >
            {msg}
          </p>
        )}

        <div className="flex flex-wrap items-center justify-between gap-2">
          <button
            onClick={() => setOpen((v) => !v)}
            className="text-xs text-brown underline"
          >
            {open ? "Hide actions" : "Actions / note"}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-accent-light bg-cream/40 p-3 flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-brown-dark">
              Admin note (sent to customer in status email)
            </label>
            <textarea
              value={adminNote}
              onChange={(e) => setAdminNote(e.target.value.slice(0, 500))}
              className="rounded border border-accent-light px-2 py-1.5 text-sm min-h-[60px] resize-y"
              placeholder="Optional note for the customer..."
            />
          </div>
          {/* Refund control — issues the Square refund AND marks the record. */}
          <div className="flex flex-col gap-1">
            <label className="text-xs font-semibold text-brown-dark">
              Refund amount (USD)
            </label>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">$</span>
              <input
                type="number"
                min="0"
                step="0.01"
                value={refundDollars}
                onChange={(e) => setRefundDollars(e.target.value)}
                className="w-28 rounded border border-accent-light px-2 py-1.5 text-sm"
                placeholder="0.00"
              />
              {suggestedCents > 0 && (
                <span className="text-[11px] text-gray-400">
                  suggested ${(suggestedCents / 100).toFixed(2)} (items only)
                </span>
              )}
            </div>
            <p className="text-[11px] text-gray-500">
              &ldquo;Refund &amp; Notify&rdquo; issues the Square refund against
              the original payment and emails the customer. Adjust the amount
              for partial returns, tax, or shipping.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => act("approve")}
              disabled={busy || req.status === "approved"}
              className="rounded bg-blue-600 text-white text-xs font-semibold px-3 py-1.5 hover:bg-blue-700 disabled:opacity-50"
            >
              {busy ? "Working…" : "Approve"}
            </button>
            <button
              onClick={() => act("deny")}
              disabled={busy || req.status === "denied"}
              className="rounded bg-amber-600 text-white text-xs font-semibold px-3 py-1.5 hover:bg-amber-700 disabled:opacity-50"
            >
              {busy ? "Working…" : "Deny"}
            </button>
            <button
              onClick={() => act("cancel")}
              disabled={
                busy || req.status === "canceled" || req.status === "refunded"
              }
              className="rounded bg-gray-500 text-white text-xs font-semibold px-3 py-1.5 hover:bg-gray-600 disabled:opacity-50"
            >
              {busy ? "Working…" : "Cancel Request"}
            </button>
            <button
              onClick={() => act("receive")}
              disabled={busy || req.status === "received"}
              className="rounded bg-purple-600 text-white text-xs font-semibold px-3 py-1.5 hover:bg-purple-700 disabled:opacity-50"
            >
              {busy ? "Working…" : "Mark Received"}
            </button>
            <button
              onClick={onRefundClick}
              disabled={busy || req.status === "refunded"}
              className="rounded bg-green-600 text-white text-xs font-semibold px-3 py-1.5 hover:bg-green-700 disabled:opacity-50"
            >
              {busy ? "Working…" : "Refund & Notify"}
            </button>
          </div>

          <ConfirmationModal
            isOpen={confirmRefund}
            onConfirm={() => {
              setConfirmRefund(false);
              act("refund");
            }}
            onCancel={() => setConfirmRefund(false)}
          >
            This return is still marked{" "}
            <strong>&ldquo;{meta.label}&rdquo;</strong> — you&apos;re refunding{" "}
            <strong>${refundDollars || "0.00"}</strong> without marking the
            item(s) received. This issues a real Square refund immediately.
            Refund anyway?
          </ConfirmationModal>
        </div>
      )}
    </div>
  );
};

const ReturnsSection: React.FC<{ authToken: string }> = ({ authToken }) => {
  const [feed, setFeed] = useState<AdminReturnRequestsFeed | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await getAdminReturnRequests(authToken);
    if (!res || res.error || !res.data) {
      setError(res?.error ?? "Failed to load return requests.");
    } else {
      setFeed(res.data);
    }
    setLoading(false);
  }, [authToken]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading)
    return <p className="text-gray-500 text-sm">Loading return requests…</p>;
  if (error) return <ErrorNote>{error}</ErrorNote>;
  if (!feed) return null;

  const shown = showAll
    ? feed.requests
    : feed.requests.filter((r) => r.status === "requested");

  return (
    <div className="flex flex-col gap-5">
      <p className="text-sm text-gray-600 lora-text max-w-4xl">
        Customer return / refund requests. Use <strong>Approve</strong> to tell
        the customer their request is accepted. When you&apos;re ready to
        refund, confirm the amount and click{" "}
        <strong>Refund &amp; Notify</strong> &mdash; it issues the Square refund
        against the original payment and emails the customer in one step. Use{" "}
        <strong>Deny</strong> with an explanatory note for requests you
        can&apos;t fulfill.
      </p>

      <div className="grid grid-cols-2 gap-3 max-w-sm">
        <SummaryStat
          label="Awaiting review"
          value={feed.pendingCount}
          accent={feed.pendingCount > 0 ? "text-amber-600" : undefined}
        />
        <SummaryStat label="Total requests" value={feed.count} />
      </div>

      <div className="flex items-center justify-between">
        <label className="text-xs text-gray-500 flex items-center gap-1">
          <input
            type="checkbox"
            checked={showAll}
            onChange={(e) => setShowAll(e.target.checked)}
          />
          Show resolved requests
        </label>
        <button onClick={load} className="btn-secondary text-sm">
          Refresh
        </button>
      </div>

      {shown.length === 0 ? (
        <div className="rounded-lg border border-accent-light bg-white p-6 text-center text-gray-500 text-sm">
          {showAll ? "No return requests yet." : "No pending requests. 🎉"}
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {shown.map((req) => (
            <ReturnRequestRow
              key={req.id}
              req={req}
              authToken={authToken}
              onUpdated={load}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Section: Reviews (moderation queue — pending reviews are invisible on the
// storefront until published here)
// ---------------------------------------------------------------------------

const REVIEW_STATUS_META: Record<
  AdminReview["status"],
  { label: string; chip: string }
> = {
  pending: { label: "Pending", chip: "bg-amber-100 text-amber-800" },
  published: { label: "Published", chip: "bg-green-100 text-green-800" },
  rejected: { label: "Rejected", chip: "bg-red-100 text-red-800" },
};

const ReviewStars: React.FC<{ value: number }> = ({ value }) => (
  <span className="whitespace-nowrap text-sm">
    {[1, 2, 3, 4, 5].map((star) => (
      <span
        key={star}
        className={star <= value ? "text-yellow-500" : "text-gray-300"}
      >
        ★
      </span>
    ))}
  </span>
);

const ReviewsSection: React.FC<{ authToken: string }> = ({ authToken }) => {
  const [feed, setFeed] = useState<AdminReviewsFeed | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actingOn, setActingOn] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await getAdminReviews(authToken);
    if (!res || res.error || !res.data) {
      setError(res?.error ?? "Failed to load reviews.");
    } else {
      setFeed(res.data);
    }
    setLoading(false);
  }, [authToken]);

  useEffect(() => {
    load();
  }, [load]);

  const handleModerate = async (
    review: AdminReview,
    action: "publish" | "reject" | "delete",
  ) => {
    if (
      action === "delete" &&
      !window.confirm("Permanently delete this review?")
    ) {
      return;
    }
    setActingOn(review.id);
    setError(null);
    const res = await moderateAdminReview(authToken, review.id, action);
    if (!res || res.error) {
      setError(res?.error ?? "Failed to update review.");
    } else {
      await load();
    }
    setActingOn(null);
  };

  const pending = feed?.reviews.filter((r) => r.status === "pending") ?? [];
  const rest = feed?.reviews.filter((r) => r.status !== "pending") ?? [];

  const ReviewRow: React.FC<{ review: AdminReview }> = ({ review }) => {
    const meta = REVIEW_STATUS_META[review.status];
    const busy = actingOn === review.id;
    return (
      <div className="rounded-lg border border-accent-light bg-white p-4 flex flex-col gap-2">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <ReviewStars value={review.rating} />
            <span className="text-sm font-semibold">{review.author}</span>
            <span
              className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${meta.chip}`}
            >
              {meta.label}
            </span>
          </div>
          <span className="text-xs text-gray-400">
            {new Date(review.createdAt).toLocaleString()}
          </span>
        </div>
        <div className="text-xs text-gray-500">
          {review.itemName || review.itemId} · order{" "}
          <span translate="no">{review.orderId}</span>
        </div>
        {review.text && (
          <p className="text-sm text-gray-700 whitespace-pre-wrap">
            {review.text}
          </p>
        )}
        <div className="flex gap-2">
          {review.status !== "published" && (
            <button
              disabled={busy}
              onClick={() => handleModerate(review, "publish")}
              className="rounded bg-green-600 text-white text-xs font-semibold px-3 py-1.5 hover:bg-green-700 disabled:opacity-50"
            >
              Publish
            </button>
          )}
          {review.status !== "rejected" && (
            <button
              disabled={busy}
              onClick={() => handleModerate(review, "reject")}
              className="rounded bg-amber-600 text-white text-xs font-semibold px-3 py-1.5 hover:bg-amber-700 disabled:opacity-50"
            >
              Reject
            </button>
          )}
          <button
            disabled={busy}
            onClick={() => handleModerate(review, "delete")}
            className="rounded bg-gray-200 text-gray-700 text-xs font-semibold px-3 py-1.5 hover:bg-gray-300 disabled:opacity-50"
          >
            Delete
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl playfair-text text-brown-dark">
          Product Reviews
        </h2>
        <button
          onClick={load}
          disabled={loading}
          className="rounded bg-brown text-white text-xs font-semibold px-3 py-1.5 hover:bg-brown-dark disabled:opacity-50"
        >
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      {error && <ErrorNote>{error}</ErrorNote>}

      {feed && (
        <div className="grid grid-cols-2 gap-3 max-w-sm">
          <SummaryStat
            label="Awaiting approval"
            value={feed.pendingCount}
            accent={feed.pendingCount > 0 ? "text-amber-600" : undefined}
          />
          <SummaryStat label="Total reviews" value={feed.count} />
        </div>
      )}

      {!loading && feed && feed.count === 0 && (
        <p className="text-sm text-gray-500">
          No reviews yet. Verified buyers can leave one from their account's
          order page.
        </p>
      )}

      {pending.length > 0 && (
        <div className="flex flex-col gap-2">
          <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide">
            Awaiting approval
          </h3>
          {pending.map((r) => (
            <ReviewRow key={r.id} review={r} />
          ))}
        </div>
      )}

      {rest.length > 0 && (
        <div className="flex flex-col gap-2">
          <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wide">
            Moderated
          </h3>
          {rest.map((r) => (
            <ReviewRow key={r.id} review={r} />
          ))}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Section: Product Management (Site Visibility + SKU Sync)
//
// One home for the post-Printful "finish a product" work. Adding a product is
// done IN Printful: this is a Square-platform store, so Printful creates the
// Square item + variations and writes external_id itself — there is deliberately
// no "create product" step here. What's left (SKU suffix sync, visibility) lives
// here. Inner sub-tabs keep the two heavy catalog loads from firing at once.
// ---------------------------------------------------------------------------

type ProductSubTab = "visibility" | "groups" | "sync" | "controls";

// Rendered tab row AND the `?sub=` allow-list — one list so a new tab can't be
// clickable but unaddressable (a value missing here falls back to "visibility",
// which is exactly how Homepage Groups shipped un-deep-linkable).
const PRODUCT_SUB_TABS_META: { id: ProductSubTab; label: string }[] = [
  { id: "visibility", label: "Site Visibility" },
  { id: "groups", label: "Homepage Groups" },
  { id: "sync", label: "Catalog Sync" },
  { id: "controls", label: "Site Controls" },
];

const PRODUCT_SUB_TABS: ProductSubTab[] = PRODUCT_SUB_TABS_META.map(
  (t) => t.id,
);

const ProductManagementSection: React.FC<{ authToken: string }> = ({
  authToken,
}) => {
  const subTabs = PRODUCT_SUB_TABS_META;

  const [sub, setSub] = useTabParam<ProductSubTab>(
    ADMIN_SUBTAB_PARAM,
    PRODUCT_SUB_TABS,
    "visibility",
  );

  return (
    <div className="flex flex-col gap-5">
      <details className="rounded-lg border border-accent-light bg-cream/40 p-3 text-sm text-gray-600 lora-text">
        <summary className="cursor-pointer font-semibold text-brown-dark">
          How to add a new product
        </summary>
        <ol className="list-decimal ml-5 mt-2 space-y-1.5">
          <li>
            Design it in <span className="font-semibold">Printful</span> (pick
            the blank, upload artwork, place the print, generate mockups).
            Because this is a Square-platform store, Printful creates the
            matching Square item and variations for you and links them back
            automatically — there's no "create product" step here on purpose.
          </li>
          <li>
            Come back and run{" "}
            <span className="font-semibold">Catalog Sync</span> —{" "}
            <span className="font-semibold">SKU Sync</span> (so each variation's
            SKU points at the right Printful sync variant, which makes an order
            fulfill as the correct design) and{" "}
            <span className="font-semibold">Swatch Sync</span> (so the colour
            bubbles show the real blank colours). New variations need a SKU with
            a <code>_</code> design-code prefix first; SKU Sync only rewrites
            the suffix after the <code>_</code>.
          </li>
          <li>
            In Square, set the new item's{" "}
            <span className="font-semibold">category</span> (a child category
            whose parent is top-level, or it gets no canonical URL and is
            invisible to Google/Meta), description, and images. Also set the
            Color option's display to <strong>“Text and color”</strong>.
          </li>
          <li>
            Keep it hidden in{" "}
            <span className="font-semibold">Site Visibility</span> until it's
            ready, then flip it to visible — and run{" "}
            <span className="font-semibold">Site Controls → Refresh cache</span>{" "}
            so visitors see it right away. It joins the homepage's{" "}
            <span className="font-semibold">Designs</span> carousel
            automatically; only tick it in{" "}
            <span className="font-semibold">Homepage Groups</span> if it belongs
            with the Classics.
          </li>
        </ol>
      </details>

      <details className="rounded-lg border border-accent-light bg-cream/40 p-3 text-sm text-gray-600 lora-text">
        <summary className="cursor-pointer font-semibold text-brown-dark">
          How to add a colour, or replace a product's blank
        </summary>
        <p className="mt-2">
          <strong>
            Printful can't add a colour (or change the blank) on an existing
            product.
          </strong>{" "}
          Its design editor only edits the artwork/mockups of the variants that
          already exist — there's no “add variants”. To add a colour you build a{" "}
          <em>new</em> product done right and swap it in. (Adding the colour
          directly in Square instead would create a variation with no Printful
          sync variant — it can't be fulfilled and voids at checkout.)
        </p>
        <ol className="list-decimal ml-5 mt-2 space-y-1.5">
          <li>
            In Printful,{" "}
            <span className="font-semibold">
              create a new product on the SAME blank
            </span>{" "}
            (e.g. Comfort Colors 1717 — don't switch blanks), upload the{" "}
            <span className="font-semibold">same design</span> at the same
            placement, and select{" "}
            <span className="font-semibold">
              every colour — the existing ones plus the new one
            </span>{" "}
            — and all sizes. Give it the <strong>exact same name</strong> and
            the same price. Do <strong>not</strong> use “Duplicate” or a
            different blank — that's how you get a mismatched/orphan product.
          </li>
          <li>
            It syncs to Square as a{" "}
            <span className="font-semibold">new item</span> (you'll briefly have
            two same-named items). Match its{" "}
            <span className="font-semibold">category chain</span>, description,
            and images to the old one so the <strong>URL is identical</strong> —
            no 404, no lost SEO.
          </li>
          <li>
            Run <span className="font-semibold">Catalog Sync</span> (SKU +
            Swatch) on the new item, and set its Color option to{" "}
            <strong>“Text and color”</strong>.
          </li>
          <li>
            <span className="font-semibold">Delete the OLD item</span> (the one
            missing the colour) in Square. If it has product reviews, migrate
            them first — they're keyed by item id and don't follow
            automatically.
          </li>
          <li>
            Run{" "}
            <span className="font-semibold">Site Controls → Refresh cache</span>{" "}
            so every visitor picks up the swap immediately.
          </li>
        </ol>
      </details>

      <div className="flex gap-1 border-b border-accent-light">
        {subTabs.map((s) => (
          <button
            key={s.id}
            onClick={() => setSub(s.id)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-semibold transition-colors ${
              sub === s.id
                ? "border-brown text-brown-dark"
                : "border-transparent text-gray-500 hover:text-brown"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {sub === "visibility" && <SiteVisibilitySection authToken={authToken} />}

      {sub === "groups" && <HomepageGroupsSection authToken={authToken} />}

      {sub === "sync" && (
        <div className="flex flex-col gap-8">
          <div className="rounded-lg bg-cream/60 border border-accent-light p-3 text-sm text-gray-700 lora-text">
            Run both of these{" "}
            <strong>after every Printful catalog change</strong> (new product,
            colour, or rename). The Printful→Square sync rewrites SKUs and wipes
            colour swatches, so both drift together. Each step previews first,
            then applies.
          </div>
          <div>
            <h3 className="text-base font-semibold text-brown-dark mb-2">
              1. SKU Sync
            </h3>
            <SkuSyncSection authToken={authToken} />
          </div>
          <div className="border-t border-accent-light pt-6">
            <h3 className="text-base font-semibold text-brown-dark mb-2">
              2. Swatch Sync
            </h3>
            <SwatchSyncSection authToken={authToken} />
          </div>
        </div>
      )}

      {sub === "controls" && <SiteControlsSection authToken={authToken} />}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Section: Promo Codes (code CRUD + salesperson roster + performance report)
//
// Codes live in Netlify Blobs (admin-promos) and are merged with the built-ins
// by the shared resolver, so a code created here is honored at checkout exactly
// like AMERICA250. The performance tab recomputes dollars from real Square
// orders (admin-promo-report) — it never trusts a stored counter.
// ---------------------------------------------------------------------------

type PromoSubTab = "codes" | "people" | "performance";

const PROMO_SUB_TABS: PromoSubTab[] = ["codes", "people", "performance"];

/** Human discount label, e.g. "25%" or "$5.00". */
const discountLabel = (c: {
  discountType: string;
  discountPercentage: string | null;
  discountAmountCents: number | null;
}): string =>
  c.discountType === "fixed"
    ? money(c.discountAmountCents ?? 0)
    : `${c.discountPercentage ?? "0"}%`;

interface CodeFormState {
  code: string;
  discountType: "percentage" | "fixed";
  discountPercentage: string;
  discountDollars: string;
  displayName: string;
  active: boolean;
  validFrom: string;
  validUntil: string;
  maxRedemptions: string;
  perCustomerLimit: string;
  salespersonId: string;
  editing: boolean;
  originalSalespersonId: string | null;
}

const emptyCodeForm = (): CodeFormState => ({
  code: "",
  discountType: "percentage",
  discountPercentage: "10",
  discountDollars: "5.00",
  displayName: "",
  active: true,
  validFrom: "",
  validUntil: "",
  maxRedemptions: "",
  perCustomerLimit: "",
  salespersonId: "",
  editing: false,
  originalSalespersonId: null,
});

const PromoCodesCrud: React.FC<{
  authToken: string;
  codes: AdminPromoCode[];
  salespeople: AdminSalesperson[];
  reload: () => Promise<void>;
}> = ({ authToken, codes, salespeople, reload }) => {
  const [form, setForm] = useState<CodeFormState | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyCode, setBusyCode] = useState<string | null>(null);

  const personName = (id: string | null) =>
    id ? (salespeople.find((p) => p.id === id)?.name ?? "(deleted)") : "—";

  const startCreate = () => {
    setError(null);
    setForm(emptyCodeForm());
  };

  const startEdit = (c: AdminPromoCode) => {
    setError(null);
    setForm({
      code: c.code,
      discountType: c.discountType,
      discountPercentage: c.discountPercentage ?? "10",
      discountDollars: ((c.discountAmountCents ?? 500) / 100).toFixed(2),
      displayName: c.displayName,
      active: c.active,
      validFrom: c.validFrom ?? "",
      validUntil: c.validUntil ?? "",
      maxRedemptions: c.maxRedemptions != null ? String(c.maxRedemptions) : "",
      perCustomerLimit:
        c.perCustomerLimit != null ? String(c.perCustomerLimit) : "",
      salespersonId: c.salespersonId ?? "",
      editing: true,
      originalSalespersonId: c.salespersonId,
    });
  };

  const submit = async () => {
    if (!form) return;
    // Warn before reassigning a code with sales history to a different person —
    // the report re-credits the code's ENTIRE history to the new owner.
    if (
      form.editing &&
      form.salespersonId !== (form.originalSalespersonId ?? "")
    ) {
      const to = form.salespersonId ? personName(form.salespersonId) : "no one";
      const okToReassign = window.confirm(
        `Reassigning ${form.code} re-credits this code's entire past sales history to ${to} in the performance report. Continue?`,
      );
      if (!okToReassign) return;
    }
    setSaving(true);
    setError(null);
    const payload: PromoCodeInput = {
      code: form.code,
      discountType: form.discountType,
      displayName: form.displayName || form.code.toUpperCase(),
      active: form.active,
      validFrom: form.validFrom || null,
      validUntil: form.validUntil || null,
      maxRedemptions: form.maxRedemptions ? Number(form.maxRedemptions) : null,
      perCustomerLimit: form.perCustomerLimit
        ? Number(form.perCustomerLimit)
        : null,
      salespersonId: form.salespersonId || null,
      ...(form.discountType === "fixed"
        ? {
            discountAmountCents: Math.round(Number(form.discountDollars) * 100),
          }
        : { discountPercentage: form.discountPercentage }),
    };
    const res = await savePromoCode(authToken, payload);
    setSaving(false);
    if (!res || res.error || !res.data) {
      setError(res?.error ?? "Failed to save the code.");
      return;
    }
    setForm(null);
    await reload();
  };

  const remove = async (code: string) => {
    if (!window.confirm(`Delete promo code ${code}? This can't be undone.`))
      return;
    setBusyCode(code);
    setError(null);
    const res = await deletePromoCode(authToken, code);
    setBusyCode(null);
    if (!res || res.error) {
      setError(res?.error ?? "Failed to delete the code.");
      return;
    }
    await reload();
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600 lora-text max-w-4xl">
          Codes are honored at checkout immediately. A code assigned to a
          salesperson attributes its sales to them in the Performance tab.
        </p>
        {!form && (
          <button onClick={startCreate} className="btn-primary text-sm">
            New code
          </button>
        )}
      </div>

      {error && <ErrorNote>{error}</ErrorNote>}

      {form && (
        <div className="rounded-lg border border-accent-light bg-cream/40 p-4 flex flex-col gap-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-semibold text-brown-dark">Code</span>
              <input
                type="text"
                value={form.code}
                disabled={form.editing}
                translate="no"
                onChange={(e) =>
                  setForm({ ...form, code: e.target.value.toUpperCase() })
                }
                className="rounded-md border border-accent-light px-2 py-1 disabled:bg-gray-100 uppercase"
                placeholder="SUMMER25"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-semibold text-brown-dark">
                Display name
              </span>
              <input
                type="text"
                value={form.displayName}
                onChange={(e) =>
                  setForm({ ...form, displayName: e.target.value })
                }
                className="rounded-md border border-accent-light px-2 py-1"
                placeholder="Summer Sale – 25% Off"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-semibold text-brown-dark">
                Discount type
              </span>
              <select
                value={form.discountType}
                onChange={(e) =>
                  setForm({
                    ...form,
                    discountType: e.target.value as "percentage" | "fixed",
                  })
                }
                className="rounded-md border border-accent-light px-2 py-1 bg-white"
              >
                <option value="percentage">Percentage (%)</option>
                <option value="fixed">Fixed amount ($)</option>
              </select>
            </label>
            {form.discountType === "percentage" ? (
              <label className="flex flex-col gap-1 text-sm">
                <span className="font-semibold text-brown-dark">
                  Percent off
                </span>
                <input
                  type="number"
                  min={1}
                  max={100}
                  value={form.discountPercentage}
                  onChange={(e) =>
                    setForm({ ...form, discountPercentage: e.target.value })
                  }
                  className="rounded-md border border-accent-light px-2 py-1"
                />
              </label>
            ) : (
              <label className="flex flex-col gap-1 text-sm">
                <span className="font-semibold text-brown-dark">
                  Dollars off
                </span>
                <input
                  type="number"
                  min={0.01}
                  step={0.01}
                  value={form.discountDollars}
                  onChange={(e) =>
                    setForm({ ...form, discountDollars: e.target.value })
                  }
                  className="rounded-md border border-accent-light px-2 py-1"
                />
              </label>
            )}
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-semibold text-brown-dark">Salesperson</span>
              <select
                value={form.salespersonId}
                onChange={(e) =>
                  setForm({ ...form, salespersonId: e.target.value })
                }
                className="rounded-md border border-accent-light px-2 py-1 bg-white"
              >
                <option value="">— none —</option>
                {salespeople.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>
            {/* The two caps are independent and easy to mix up, so each says
                plainly whose limit it is. "First 50 people, once each" is
                total 50 + per-customer 1; a public once-per-person code is
                per-customer 1 with the total left blank. */}
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-semibold text-brown-dark">
                Total redemptions (everyone)
              </span>
              <input
                type="number"
                min={1}
                value={form.maxRedemptions}
                onChange={(e) =>
                  setForm({ ...form, maxRedemptions: e.target.value })
                }
                className="rounded-md border border-accent-light px-2 py-1"
                placeholder="unlimited"
              />
              <span className="text-xs text-gray-500">
                Uses across all customers combined. Once reached the code stops
                working for everybody. Blank = unlimited.
              </span>
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-semibold text-brown-dark">
                Uses per customer
              </span>
              <input
                type="number"
                min={1}
                value={form.perCustomerLimit}
                onChange={(e) =>
                  setForm({ ...form, perCustomerLimit: e.target.value })
                }
                className="rounded-md border border-accent-light px-2 py-1"
                placeholder="unlimited"
              />
              <span className="text-xs text-gray-500">
                How many times one buyer may use it. Set <strong>1</strong> for
                a code anyone can have but each person can redeem only once.
                Blank = unlimited. Counted per email address, at capture.
              </span>
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-semibold text-brown-dark">Valid from</span>
              <input
                type="date"
                value={form.validFrom}
                onChange={(e) =>
                  setForm({ ...form, validFrom: e.target.value })
                }
                className="rounded-md border border-accent-light px-2 py-1"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-semibold text-brown-dark">Valid until</span>
              <input
                type="date"
                value={form.validUntil}
                onChange={(e) =>
                  setForm({ ...form, validUntil: e.target.value })
                }
                className="rounded-md border border-accent-light px-2 py-1"
              />
            </label>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
            />
            <span className="text-brown-dark">Active</span>
          </label>
          <div className="flex items-center gap-2">
            <button
              onClick={submit}
              disabled={saving || !form.code.trim()}
              className="btn-primary text-sm disabled:opacity-50"
            >
              {saving
                ? "Saving…"
                : form.editing
                  ? "Save changes"
                  : "Create code"}
            </button>
            <button
              onClick={() => setForm(null)}
              className="btn-secondary text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-accent-light">
        <table className="w-full text-sm">
          <thead className="bg-cream text-left text-gray-600">
            <tr>
              <th className="px-3 py-2 font-semibold">Code</th>
              <th className="px-3 py-2 font-semibold">Discount</th>
              <th className="px-3 py-2 font-semibold">Salesperson</th>
              <th className="px-3 py-2 font-semibold">Total used</th>
              <th className="px-3 py-2 font-semibold">Per customer</th>
              <th className="px-3 py-2 font-semibold">Status</th>
              <th className="px-3 py-2 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {codes.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-4 text-center text-gray-500">
                  No codes yet. Built-in codes (AMERICA250, owner) still work.
                </td>
              </tr>
            )}
            {codes.map((c) => (
              <tr key={c.code} className="border-t border-accent-light">
                <td
                  className="px-3 py-2 font-mono font-semibold text-brown-dark"
                  translate="no"
                >
                  {c.code}
                </td>
                <td className="px-3 py-2">{discountLabel(c)}</td>
                <td className="px-3 py-2 text-gray-600">
                  {personName(c.salespersonId)}
                </td>
                <td className="px-3 py-2 text-gray-600">
                  {c.redemptionCount}
                  {c.maxRedemptions != null ? ` / ${c.maxRedemptions}` : ""}
                </td>
                <td className="px-3 py-2 text-gray-600">
                  {c.perCustomerLimit != null
                    ? c.perCustomerLimit === 1
                      ? "once each"
                      : `${c.perCustomerLimit}× each`
                    : "unlimited"}
                </td>
                <td className="px-3 py-2">
                  {c.active ? (
                    <span className="rounded-full bg-green-100 text-green-800 px-2 py-0.5 text-xs font-semibold">
                      Active
                    </span>
                  ) : (
                    <span className="rounded-full bg-gray-100 text-gray-600 px-2 py-0.5 text-xs font-semibold">
                      Off
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <button
                    onClick={() => startEdit(c)}
                    className="btn-secondary text-xs mr-2"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => remove(c.code)}
                    disabled={busyCode === c.code}
                    className="text-xs text-rust hover:underline disabled:opacity-50"
                  >
                    {busyCode === c.code ? "…" : "Delete"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const SalespeopleCrud: React.FC<{
  authToken: string;
  salespeople: AdminSalesperson[];
  codes: AdminPromoCode[];
  reload: () => Promise<void>;
}> = ({ authToken, salespeople, codes, reload }) => {
  const [form, setForm] = useState<{
    id?: string;
    name: string;
    email: string;
    active: boolean;
  } | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const codeCount = (id: string) =>
    codes.filter((c) => c.salespersonId === id).length;

  const submit = async () => {
    if (!form || !form.name.trim()) return;
    setSaving(true);
    setError(null);
    const res = await saveSalesperson(authToken, form);
    setSaving(false);
    if (!res || res.error || !res.data) {
      setError(res?.error ?? "Failed to save the salesperson.");
      return;
    }
    setForm(null);
    await reload();
  };

  const remove = async (p: AdminSalesperson) => {
    if (!window.confirm(`Delete salesperson ${p.name}?`)) return;
    setBusyId(p.id);
    setError(null);
    const res = await deleteSalesperson(authToken, p.id);
    setBusyId(null);
    if (!res || res.error) {
      setError(res?.error ?? "Failed to delete the salesperson.");
      return;
    }
    await reload();
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600 lora-text max-w-4xl">
          The roster is used to attribute promo-code sales. This tracks what
          each person sold — payouts and 1099s are handled in your accounting.
        </p>
        {!form && (
          <button
            onClick={() => setForm({ name: "", email: "", active: true })}
            className="btn-primary text-sm"
          >
            New salesperson
          </button>
        )}
      </div>

      {error && <ErrorNote>{error}</ErrorNote>}

      {form && (
        <div className="rounded-lg border border-accent-light bg-cream/40 p-4 flex flex-col gap-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-semibold text-brown-dark">Name</span>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="rounded-md border border-accent-light px-2 py-1"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-semibold text-brown-dark">Email</span>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="rounded-md border border-accent-light px-2 py-1"
              />
            </label>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
            />
            <span className="text-brown-dark">Active</span>
          </label>
          <div className="flex items-center gap-2">
            <button
              onClick={submit}
              disabled={saving || !form.name.trim()}
              className="btn-primary text-sm disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save"}
            </button>
            <button
              onClick={() => setForm(null)}
              className="btn-secondary text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-accent-light">
        <table className="w-full text-sm">
          <thead className="bg-cream text-left text-gray-600">
            <tr>
              <th className="px-3 py-2 font-semibold">Name</th>
              <th className="px-3 py-2 font-semibold">Email</th>
              <th className="px-3 py-2 font-semibold">Codes</th>
              <th className="px-3 py-2 font-semibold">Status</th>
              <th className="px-3 py-2 font-semibold text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {salespeople.length === 0 && (
              <tr>
                <td colSpan={5} className="px-3 py-4 text-center text-gray-500">
                  No salespeople yet.
                </td>
              </tr>
            )}
            {salespeople.map((p) => (
              <tr key={p.id} className="border-t border-accent-light">
                <td className="px-3 py-2 font-semibold text-brown-dark">
                  {p.name}
                </td>
                <td className="px-3 py-2 text-gray-600">{p.email || "—"}</td>
                <td className="px-3 py-2 text-gray-600">{codeCount(p.id)}</td>
                <td className="px-3 py-2">
                  {p.active ? (
                    <span className="rounded-full bg-green-100 text-green-800 px-2 py-0.5 text-xs font-semibold">
                      Active
                    </span>
                  ) : (
                    <span className="rounded-full bg-gray-100 text-gray-600 px-2 py-0.5 text-xs font-semibold">
                      Off
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <button
                    onClick={() =>
                      setForm({
                        id: p.id,
                        name: p.name,
                        email: p.email,
                        active: p.active,
                      })
                    }
                    className="btn-secondary text-xs mr-2"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => remove(p)}
                    disabled={busyId === p.id}
                    className="text-xs text-rust hover:underline disabled:opacity-50"
                  >
                    {busyId === p.id ? "…" : "Delete"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Live promo report window options. Deliberately NO "All time": an all-time
// scan can fan out to thousands of Square calls, so older history is served from
// the quarterly archive (the selector below) instead. Kept separate from the
// sales-dashboard RANGE_OPTIONS so that one can keep its "All time".
const PROMO_RANGE_OPTIONS: { value: number; label: string }[] = [
  { value: 7, label: "Last 7 days" },
  { value: 30, label: "Last 30 days" },
  { value: 90, label: "Last 90 days" },
  { value: 365, label: "Last year" },
];

const PromoPerformance: React.FC<{ authToken: string }> = ({ authToken }) => {
  const [data, setData] = useState<AdminPromoReport | null>(null);
  const [days, setDays] = useState(30);
  // "" = live (days-based); otherwise a "YYYY-Qn" archived quarter (no Square hit).
  const [quarter, setQuarter] = useState("");
  const [archives, setArchives] = useState<AdminPromoArchiveEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load the archived-quarter index once (cheap; served from Blobs).
  useEffect(() => {
    let cancelled = false;
    getPromoArchiveIndex(authToken).then((res) => {
      if (!cancelled && res?.data?.archives) setArchives(res.data.archives);
    });
    return () => {
      cancelled = true;
    };
  }, [authToken]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    if (quarter) {
      const res = await getPromoArchive(authToken, quarter);
      if (!res || res.error || !res.data) {
        setError(res?.error ?? "Failed to load the archived quarter.");
      } else {
        // Reshape the archived snapshot into the live report shape the tables use.
        const s = res.data;
        setData({
          environment: s.environment,
          days: 0,
          generatedAt: s.generatedAt,
          ...s.report,
        });
      }
    } else {
      const res = await getAdminPromoReport(authToken, days);
      if (!res || res.error || !res.data) {
        setError(res?.error ?? "Failed to load the promo report.");
      } else {
        setData(res.data);
      }
    }
    setLoading(false);
  }, [authToken, days, quarter]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading && !data)
    return <p className="text-gray-500 text-sm">Loading promo performance…</p>;
  if (error && !data) return <ErrorNote>{error}</ErrorNote>;
  if (!data) return null;

  return (
    <div className="flex flex-col gap-5">
      <p className="text-sm text-gray-600 lora-text max-w-4xl">
        Collected sales ({data.environment}) attributed to each promo code and
        salesperson. Revenue is recomputed from Square — captured, non-refunded
        orders only — so it matches the money actually earned. Use it to see
        what each person sold; payouts and 1099s live in your accounting.
      </p>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-brown-dark">Range</span>
          <select
            value={days}
            disabled={Boolean(quarter)}
            onChange={(e) => setDays(Number(e.target.value))}
            className="rounded-md border border-accent-light bg-white px-2 py-1 text-sm disabled:opacity-50"
          >
            {PROMO_RANGE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>

          {archives.length > 0 && (
            <>
              <span className="text-sm font-semibold text-brown-dark">
                or archived quarter
              </span>
              <select
                value={quarter}
                onChange={(e) => setQuarter(e.target.value)}
                className="rounded-md border border-accent-light bg-white px-2 py-1 text-sm"
                translate="no"
              >
                <option value="">Live report</option>
                {archives.map((a) => (
                  <option key={a.quarter} value={a.quarter}>
                    {a.quarter}
                  </option>
                ))}
              </select>
            </>
          )}
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="btn-secondary text-sm disabled:opacity-50"
        >
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      {quarter && (
        <p className="text-xs text-gray-500">
          Viewing the archived <span translate="no">{quarter}</span> snapshot —
          captured at report time, no live recompute. Switch to “Live report”
          for the current window.
        </p>
      )}

      {error && <ErrorNote>{error}</ErrorNote>}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <SummaryStat
          label="Attributed orders"
          value={data.attributedOrderCount}
        />
        <SummaryStat label="Units" value={data.totals.units} />
        <SummaryStat
          label="Revenue"
          value={money(data.totals.grossRevenueCents)}
          accent="text-green-700"
        />
        <SummaryStat
          label="Discounts given"
          value={money(data.totals.discountCents)}
        />
      </div>

      {data.truncated && (
        <p className="text-xs text-amber-600">
          Showing the most recent orders only — narrow the range for the full
          window.
        </p>
      )}

      {/* Per salesperson */}
      <div>
        <h3 className="text-sm font-semibold text-brown-dark mb-2">
          By salesperson
        </h3>
        <div className="overflow-x-auto rounded-lg border border-accent-light">
          <table className="w-full text-sm">
            <thead className="bg-cream text-left text-gray-600">
              <tr>
                <th className="px-3 py-2 font-semibold">Salesperson</th>
                <th className="px-3 py-2 font-semibold">Codes</th>
                <th className="px-3 py-2 font-semibold text-right">Orders</th>
                <th className="px-3 py-2 font-semibold text-right">Units</th>
                <th className="px-3 py-2 font-semibold text-right">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {data.salespeople.length === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    className="px-3 py-4 text-center text-gray-500"
                  >
                    No attributed sales in this window.
                  </td>
                </tr>
              )}
              {data.salespeople.map((s) => (
                <tr
                  key={s.salespersonId ?? "unassigned"}
                  className="border-t border-accent-light"
                >
                  <td className="px-3 py-2 font-semibold text-brown-dark">
                    {s.salespersonName}
                  </td>
                  <td className="px-3 py-2 text-gray-500" translate="no">
                    {s.codes.join(", ")}
                  </td>
                  <td className="px-3 py-2 text-right text-gray-600">
                    {s.orders}
                  </td>
                  <td className="px-3 py-2 text-right text-gray-600">
                    {s.units}
                  </td>
                  <td className="px-3 py-2 text-right font-semibold">
                    {money(s.grossRevenueCents)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Per code */}
      <div>
        <h3 className="text-sm font-semibold text-brown-dark mb-2">By code</h3>
        <div className="overflow-x-auto rounded-lg border border-accent-light">
          <table className="w-full text-sm">
            <thead className="bg-cream text-left text-gray-600">
              <tr>
                <th className="px-3 py-2 font-semibold">Code</th>
                <th className="px-3 py-2 font-semibold">Salesperson</th>
                <th className="px-3 py-2 font-semibold text-right">Orders</th>
                <th className="px-3 py-2 font-semibold text-right">Units</th>
                <th className="px-3 py-2 font-semibold text-right">Revenue</th>
                <th className="px-3 py-2 font-semibold text-right">
                  Discounts
                </th>
              </tr>
            </thead>
            <tbody>
              {data.codes.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-3 py-4 text-center text-gray-500"
                  >
                    No attributed sales in this window.
                  </td>
                </tr>
              )}
              {data.codes.map((c) => (
                <tr key={c.code} className="border-t border-accent-light">
                  <td
                    className="px-3 py-2 font-mono font-semibold text-brown-dark"
                    translate="no"
                  >
                    {c.code}
                    {c.retired && (
                      <span className="ml-1 text-[10px] text-amber-600">
                        (retired)
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-gray-600">
                    {c.salespersonName ?? "—"}
                  </td>
                  <td className="px-3 py-2 text-right text-gray-600">
                    {c.orders}
                  </td>
                  <td className="px-3 py-2 text-right text-gray-600">
                    {c.units}
                  </td>
                  <td className="px-3 py-2 text-right font-semibold">
                    {money(c.grossRevenueCents)}
                  </td>
                  <td className="px-3 py-2 text-right text-gray-500">
                    {money(c.discountCents)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const PromoCodesSection: React.FC<{ authToken: string }> = ({ authToken }) => {
  const [sub, setSub] = useTabParam<PromoSubTab>(
    ADMIN_SUBTAB_PARAM,
    PROMO_SUB_TABS,
    "codes",
  );
  const [codes, setCodes] = useState<AdminPromoCode[]>([]);
  const [salespeople, setSalespeople] = useState<AdminSalesperson[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const res = await getAdminPromos(authToken);
    if (!res || res.error || !res.data) {
      setError(res?.error ?? "Failed to load promo data.");
    } else {
      setCodes(res.data.codes);
      setSalespeople(res.data.salespeople);
    }
    setLoading(false);
  }, [authToken]);

  useEffect(() => {
    load();
  }, [load]);

  const subTabs: { id: PromoSubTab; label: string }[] = [
    { id: "codes", label: "Codes" },
    { id: "people", label: "Salespeople" },
    { id: "performance", label: "Performance" },
  ];

  return (
    <div className="flex flex-col gap-5">
      <div className="flex gap-1 border-b border-accent-light">
        {subTabs.map((s) => (
          <button
            key={s.id}
            onClick={() => setSub(s.id)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-semibold transition-colors ${
              sub === s.id
                ? "border-brown text-brown-dark"
                : "border-transparent text-gray-500 hover:text-brown"
            }`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {error && <ErrorNote>{error}</ErrorNote>}
      {loading && sub !== "performance" ? (
        <p className="text-gray-500 text-sm">Loading promo data…</p>
      ) : sub === "codes" ? (
        <PromoCodesCrud
          authToken={authToken}
          codes={codes}
          salespeople={salespeople}
          reload={load}
        />
      ) : sub === "people" ? (
        <SalespeopleCrud
          authToken={authToken}
          salespeople={salespeople}
          codes={codes}
          reload={load}
        />
      ) : (
        <PromoPerformance authToken={authToken} />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Traffic — where visitors come from, and what each channel actually sold
// ---------------------------------------------------------------------------
//
// The tab pairs two datasets of DIFFERENT confidence and must never blur them:
// revenue is exact (recomputed from Square), visits are an approximate
// undercount (consent-gated ping + sharded counters). Every visit-derived
// figure here is labelled as such — see docs/traffic-attribution.md.

const TRAFFIC_RANGE_OPTIONS: { value: number; label: string }[] = [
  { value: 7, label: "Last 7 days" },
  { value: 30, label: "Last 30 days" },
  { value: 90, label: "Last 90 days" },
  { value: 365, label: "Last year" },
];

/** Pretty labels for sources we classify ourselves; anything else (a referring
 *  domain, or a utm_source the owner invented) is shown verbatim. */
const SOURCE_LABELS: Record<string, string> = {
  instagram: "Instagram",
  facebook: "Facebook",
  tiktok: "TikTok",
  pinterest: "Pinterest",
  twitter: "X / Twitter",
  youtube: "YouTube",
  reddit: "Reddit",
  linkedin: "LinkedIn",
  google: "Google",
  bing: "Bing",
  duckduckgo: "DuckDuckGo",
  yahoo: "Yahoo",
  ecosia: "Ecosia",
  direct: "Direct / untagged",
  unattributed: "Unattributed",
};

const sourceLabel = (source: string): string => SOURCE_LABELS[source] ?? source;

const percent = (rate: number | null): string =>
  rate === null ? "—" : `${(rate * 100).toFixed(1)}%`;

const TrafficSection: React.FC<{ authToken: string }> = ({ authToken }) => {
  const [data, setData] = useState<AdminTrafficReport | null>(null);
  const [days, setDays] = useState(30);
  // "" = live (days-based); otherwise a "YYYY-Qn" archived quarter (no Square hit).
  const [quarter, setQuarter] = useState("");
  const [archives, setArchives] = useState<AdminTrafficArchiveEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getTrafficArchiveIndex(authToken).then((res) => {
      if (!cancelled && res?.data?.archives) setArchives(res.data.archives);
    });
    return () => {
      cancelled = true;
    };
  }, [authToken]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    if (quarter) {
      const res = await getTrafficArchive(authToken, quarter);
      if (!res || res.error || !res.data) {
        setError(res?.error ?? "Failed to load the archived quarter.");
      } else {
        const s = res.data;
        setData({
          environment: s.environment,
          days: 0,
          generatedAt: s.generatedAt,
          ...s.report,
        });
      }
    } else {
      const res = await getAdminTrafficReport(authToken, days);
      if (!res || res.error || !res.data) {
        setError(res?.error ?? "Failed to load the traffic report.");
      } else {
        setData(res.data);
      }
    }
    setLoading(false);
  }, [authToken, days, quarter]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading && !data)
    return <p className="text-gray-500 text-sm">Loading traffic report…</p>;
  if (error && !data) return <ErrorNote>{error}</ErrorNote>;
  if (!data) return null;

  const untagged = data.totals.orders - data.attributedOrderCount;

  return (
    <div className="flex flex-col gap-5">
      <p className="text-sm text-gray-600 lora-text max-w-4xl">
        Where visitors came from ({data.environment}) and what each channel
        actually sold. To track a channel, add UTM tags to the link you post —
        e.g.{" "}
        <code className="bg-cream px-1 rounded text-xs" translate="no">
          ?utm_source=instagram&amp;utm_medium=paid_social&amp;utm_campaign=july4
        </code>
        . Untagged links fall into “Direct / untagged”.
      </p>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-brown-dark">Range</span>
          <select
            value={days}
            disabled={Boolean(quarter)}
            onChange={(e) => setDays(Number(e.target.value))}
            className="rounded-md border border-accent-light bg-white px-2 py-1 text-sm disabled:opacity-50"
          >
            {TRAFFIC_RANGE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>

          {archives.length > 0 && (
            <>
              <span className="text-sm font-semibold text-brown-dark">
                or archived quarter
              </span>
              <select
                value={quarter}
                onChange={(e) => setQuarter(e.target.value)}
                className="rounded-md border border-accent-light bg-white px-2 py-1 text-sm"
                translate="no"
              >
                <option value="">Live report</option>
                {archives.map((a) => (
                  <option key={a.quarter} value={a.quarter}>
                    {a.quarter}
                  </option>
                ))}
              </select>
            </>
          )}
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="btn-secondary text-sm disabled:opacity-50"
        >
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      {quarter && (
        <p className="text-xs text-gray-500">
          Viewing the archived <span translate="no">{quarter}</span> snapshot —
          captured at report time, no live recompute.
        </p>
      )}

      {error && <ErrorNote>{error}</ErrorNote>}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <SummaryStat
          label="Visits (approx.)"
          value={data.visitsAvailable ? data.totals.visits : "—"}
        />
        <SummaryStat label="Orders" value={data.totals.orders} />
        <SummaryStat
          label="Conversion"
          value={
            data.visitsAvailable ? percent(data.totals.conversionRate) : "—"
          }
        />
        <SummaryStat
          label="Revenue"
          value={money(data.totals.grossRevenueCents)}
          accent="text-green-700"
        />
      </div>

      {/* The honesty panel. Visit-derived numbers are systematically optimistic
          and the owner has to know that before acting on them. */}
      {!data.visitsAvailable ? (
        <p className="text-xs text-amber-600">
          ⚠️ Visit counters were unavailable, so visits and conversion show “—”.
          The order and revenue columns below are unaffected — those come
          straight from Square. If this persists, check Netlify Blobs.
        </p>
      ) : (
        <p className="text-xs text-gray-500">
          Visits count only visitors who accepted cookies, so they undercount
          and conversion rates read high — compare them to each other, not as
          absolutes. Orders and revenue are exact (captured, non-refunded).
        </p>
      )}

      {data.truncated && (
        <p className="text-xs text-amber-600">
          Showing the most recent orders only — narrow the range for the full
          window.
        </p>
      )}

      {untagged > 0 && (
        <p className="text-xs text-gray-500">
          <strong>{untagged}</strong> of {data.totals.orders} orders carry no
          source tag (placed before tracking started, or by a visitor whose
          browser blocked local storage). They’re grouped under “Unattributed”
          so revenue still reconciles with the Dashboard tab.
        </p>
      )}

      {/* Per source */}
      <div>
        <h3 className="text-sm font-semibold text-brown-dark mb-2">
          By source
        </h3>
        <div className="overflow-x-auto rounded-lg border border-accent-light">
          <table className="w-full text-sm">
            <thead className="bg-cream text-left text-gray-600">
              <tr>
                <th className="px-3 py-2 font-semibold">Source</th>
                <th className="px-3 py-2 font-semibold text-right">Visits</th>
                <th className="px-3 py-2 font-semibold text-right">Orders</th>
                <th className="px-3 py-2 font-semibold text-right">Conv.</th>
                <th className="px-3 py-2 font-semibold text-right">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {data.sources.length === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    className="px-3 py-4 text-center text-gray-500"
                  >
                    No traffic or sales recorded in this window yet.
                  </td>
                </tr>
              )}
              {data.sources.map((s) => (
                <tr key={s.source} className="border-t border-accent-light">
                  <td className="px-3 py-2">
                    <span translate="no">{sourceLabel(s.source)}</span>
                    {s.mediums.length > 0 && (
                      <span
                        className="text-xs text-gray-500 ml-2"
                        translate="no"
                      >
                        {s.mediums.join(", ")}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {data.visitsAvailable ? s.visits || "—" : "—"}
                  </td>
                  <td className="px-3 py-2 text-right">{s.orders}</td>
                  <td className="px-3 py-2 text-right">
                    {data.visitsAvailable ? percent(s.conversionRate) : "—"}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {money(s.grossRevenueCents)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Per campaign — only tagged links appear here */}
      <div>
        <h3 className="text-sm font-semibold text-brown-dark mb-2">
          By campaign
        </h3>
        <div className="overflow-x-auto rounded-lg border border-accent-light">
          <table className="w-full text-sm">
            <thead className="bg-cream text-left text-gray-600">
              <tr>
                <th className="px-3 py-2 font-semibold">Campaign</th>
                <th className="px-3 py-2 font-semibold">Source / medium</th>
                <th className="px-3 py-2 font-semibold text-right">Visits</th>
                <th className="px-3 py-2 font-semibold text-right">Orders</th>
                <th className="px-3 py-2 font-semibold text-right">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {data.campaigns.length === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    className="px-3 py-4 text-center text-gray-500"
                  >
                    No campaign-tagged traffic yet. Add{" "}
                    <code translate="no">utm_campaign=</code> to your links to
                    see them broken out here.
                  </td>
                </tr>
              )}
              {data.campaigns.map((c) => (
                <tr
                  key={`${c.source}|${c.medium}|${c.campaign}`}
                  className="border-t border-accent-light"
                >
                  <td className="px-3 py-2" translate="no">
                    {c.campaign}
                  </td>
                  <td className="px-3 py-2 text-gray-600" translate="no">
                    {sourceLabel(c.source)} / {c.medium}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {data.visitsAvailable ? c.visits || "—" : "—"}
                  </td>
                  <td className="px-3 py-2 text-right">{c.orders}</td>
                  <td className="px-3 py-2 text-right">
                    {money(c.grossRevenueCents)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main AdminPage
// ---------------------------------------------------------------------------

const AdminPage: React.FC = () => {
  const navigate = useNavigate();
  const { session, isLoggedIn, isAdmin } = useCustomerAuth();
  // Which tab you're on lives in the URL (`?tab=…`, with each section's inner
  // tab in `?sub=…`), so the portal is bookmarkable and survives a refresh.
  // Changing the outer tab drops `sub` — an inner tab id is only meaningful
  // inside the section that defines it.
  const [tab, setTab] = useTabParam<AdminTab>(
    ADMIN_TAB_PARAM,
    ADMIN_TAB_IDS,
    "dashboard",
    { clears: CLEAR_ON_TAB_CHANGE },
  );
  const [isNavOpen, setIsNavOpen] = useState(false);

  // Not logged in → send to login. Logged in but not admin → bounce home.
  // (Server still enforces this on every request; these redirects are UX.)
  useEffect(() => {
    if (!isLoggedIn) {
      navigate("/account/login");
    } else if (!isAdmin) {
      navigate("/home");
    }
  }, [isLoggedIn, isAdmin, navigate]);

  if (!isLoggedIn || !isAdmin || !session) {
    return (
      <PageContainer hasPadding>
        <div className="max-w-md mx-auto text-center py-16">
          <h1 className="text-2xl playfair-text text-brown-dark">
            Not authorized
          </h1>
          <p className="text-sm text-gray-500 mt-2">
            This area is restricted to store administrators.
          </p>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer hasPadding gapSize={GapSize.Small}>
      <div className="w-full flex flex-col gap-6">
        <OrderWrapper wide>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div>
              <h1 className="text-3xl playfair-text text-brown-dark">
                Admin Portal
              </h1>
              <p className="text-sm text-gray-600 lora-text mt-1">
                Signed in as {session.email}
              </p>
            </div>
            <span className="self-start rounded-full bg-brown/10 text-brown-dark px-3 py-1 text-xs font-semibold">
              Administrator
            </span>
          </div>
        </OrderWrapper>

        <AdminNavMenu
          tab={tab}
          onSelect={setTab}
          isOpen={isNavOpen}
          onOpen={() => setIsNavOpen(true)}
          onClose={() => setIsNavOpen(false)}
        />

        <OrderWrapper wide>
          {tab === "dashboard" && (
            <SalesDashboardSection authToken={session.authToken} />
          )}
          {tab === "pricing" && (
            <PricingCostsSection authToken={session.authToken} />
          )}
          {tab === "orders" && (
            <OpenOrdersSection authToken={session.authToken} />
          )}
          {tab === "traffic" && (
            <TrafficSection authToken={session.authToken} />
          )}
          {tab === "attention" && (
            <NeedsAttentionSection authToken={session.authToken} />
          )}
          {tab === "returns" && (
            <ReturnsSection authToken={session.authToken} />
          )}
          {tab === "reviews" && (
            <ReviewsSection authToken={session.authToken} />
          )}
          {tab === "promos" && (
            <PromoCodesSection authToken={session.authToken} />
          )}
          {tab === "products" && (
            <ProductManagementSection authToken={session.authToken} />
          )}
          {tab === "printful" && (
            <PrintfulCatalogSection authToken={session.authToken} />
          )}
          {tab === "feeds" && (
            <FeedHealthSection authToken={session.authToken} />
          )}
          {tab === "tax" && <TaxBlobsSection authToken={session.authToken} />}
        </OrderWrapper>
      </div>
    </PageContainer>
  );
};

export default AdminPage;
